import React, { Fragment, useState, useRef, Children, isValidElement, cloneElement, memo, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    });
    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

/* eslint-disable max-len */
var enTranslations = {
  en: {
    a_lowercase_letter: 'a lowercase letter',
    a_number: 'a number',
    an_uppercase_letter: 'an uppercase letter',
    and_a_special_character: 'and a special character',
    at_least_8_characters: 'At least 8 characters',
    at_least_8_characters_including: 'At least 8 characters including:',
    contains_a_lowercase_letter: 'Contains a lowercase letter',
    contains_a_number: 'Contains a number',
    contains_a_special_character: 'Contains a special character',
    contains_an_uppercase_letter: 'Contains an uppercase letter',
    does_not_begin_or_end_with_blank_space: 'Does not begin or end with blank space',
    get_help: 'Get help',
    give_feedback: 'Give feedback',
    password_requirements: 'Password Requirements',
    privacy_policy: 'Privacy policy',
    sign_out: 'Sign out',
    site_icon_for: 'site icon for %{string}',
    terms_of_use: 'Terms of use',
    view_profile: 'View profile'
  }
};

var currentLocale = 'en';
var CeleratisConnector = {
  RouterLink: undefined,
  translations: enTranslations[currentLocale],
  init: function init(_ref) {
    var translations = _ref.translations,
        RouterLink = _ref.RouterLink;
    translations && this.setTranslations(translations);
    RouterLink && this.setRouterLink(RouterLink);
  },
  setTranslations: function setTranslations(translations) {
    if (translations) {
      this.translations = _objectSpread2(_objectSpread2({}, this.translations), translations);
    }
  },
  setRouterLink: function setRouterLink(Link) {
    this.RouterLink = Link;
  }
};

// Adapted from the classNames util.
// http://jedwatson.github.io/classnames
// MIT License
var hasOwn = {}.hasOwnProperty;
function classNames() {
  var classes = [];

  for (var i = 0; i < arguments.length; i++) {
    var arg = arguments[i];

    if (!arg) {
      continue;
    }

    var argType = typeof arg;

    if (argType === 'string' || argType === 'number') {
      classes.push(arg);
    } else if (Array.isArray(arg)) {
      if (arg.length) {
        var inner = classNames.apply(null, arg);

        if (inner) {
          classes.push(inner);
        }
      }
    } else if (argType === 'object') {
      if (arg.toString !== Object.prototype.toString) {
        classes.push(arg.toString());
      } else {
        for (var key in arg) {
          if (hasOwn.call(arg, key) && arg[key]) {
            classes.push(key);
          }
        }
      }
    }
  }

  return classes.join(' ');
}

var Button = function Button(_ref) {
  var type = _ref.type,
      onClick = _ref.onClick,
      _ref$disabled = _ref.disabled,
      disabled = _ref$disabled === void 0 ? false : _ref$disabled,
      to = _ref.to,
      className = _ref.className,
      children = _ref.children;

  function hasTypes(str) {
    return type && type.includes(str);
  }

  var classes = classNames(className, {
    button: true,
    'button--small': hasTypes('small'),
    'button--stretched': hasTypes('stretched'),
    'button--primary': hasTypes('primary'),
    'button--link': hasTypes('link')
  });

  if (to && CeleratisConnector.RouterLink) {
    return /*#__PURE__*/React.createElement(CeleratisConnector.RouterLink, {
      to: to,
      className: classes,
      onClick: onClick
    }, children);
  }

  return /*#__PURE__*/React.createElement("button", {
    className: classes,
    disabled: disabled,
    onClick: onClick
  }, children);
};

var SmallButton = function SmallButton(_ref) {
  var onClick = _ref.onClick,
      disabled = _ref.disabled,
      to = _ref.to,
      children = _ref.children;
  return /*#__PURE__*/React.createElement(Button, {
    type: "small",
    disabled: disabled,
    onClick: onClick,
    to: to
  }, children);
};

var LinkButton = function LinkButton(_ref) {
  var onClick = _ref.onClick,
      disabled = _ref.disabled,
      to = _ref.to,
      className = _ref.className,
      children = _ref.children;
  return /*#__PURE__*/React.createElement(Button, {
    type: "link",
    disabled: disabled,
    onClick: onClick,
    to: to,
    className: className
  }, children);
};

var StretchedButton = function StretchedButton(_ref) {
  var onClick = _ref.onClick,
      disabled = _ref.disabled,
      to = _ref.to,
      className = _ref.className,
      children = _ref.children;
  return /*#__PURE__*/React.createElement(Button, {
    type: "stretched",
    disabled: disabled,
    onClick: onClick,
    to: to,
    className: className
  }, children);
};

var PrimaryStretchedButton = function PrimaryStretchedButton(_ref) {
  var onClick = _ref.onClick,
      disabled = _ref.disabled,
      to = _ref.to,
      className = _ref.className,
      children = _ref.children;
  return /*#__PURE__*/React.createElement(Button, {
    type: "primary stretched",
    disabled: disabled,
    onClick: onClick,
    to: to,
    className: className
  }, children);
};

var PrimaryButton = function PrimaryButton(_ref) {
  var onClick = _ref.onClick,
      disabled = _ref.disabled,
      to = _ref.to,
      className = _ref.className,
      children = _ref.children;
  return /*#__PURE__*/React.createElement(Button, {
    type: "primary",
    disabled: disabled,
    onClick: onClick,
    to: to,
    className: className
  }, children);
};

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  if (typeof Symbol === "undefined" || !(Symbol.iterator in Object(arr))) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }

  return arr2;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(n);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

var I18n = {
  t: function t(name, params) {
    var string = CeleratisConnector.translations[name];

    var parseURL = function parseURL(str, key) {
      if (typeof str !== 'string') {
        return str;
      } // replace content between brackets with link


      var linkMatches = str.match(/\[(.*?)\]\((.*?)\)/); // replace content between <> with react router <Link>

      var routerLinkMatches = params && params.to && str.match(/<(.*?)>/); // If action is available and button notation marked in translation file, replace with button.

      var actionMatches = params && params.action && str.match(/<(.*?)>/);

      if (linkMatches) {
        var linkParts = str.split(linkMatches[0]);
        var link = /*#__PURE__*/React.createElement("a", {
          href: linkMatches[2],
          title: linkMatches[2]
        }, linkMatches[1]);
        return /*#__PURE__*/React.createElement(Fragment, {
          key: key
        }, linkParts[0], link, linkParts[1]);
      } else if (actionMatches) {
        var strParts = str.split(actionMatches[0]);
        var button = /*#__PURE__*/React.createElement(LinkButton, {
          onClick: params.action
        }, actionMatches[1]);
        return /*#__PURE__*/React.createElement(Fragment, {
          key: key
        }, strParts[0], button, strParts[1]);
      } else if (routerLinkMatches && CeleratisConnector.RouterLink) {
        var _strParts = str.split(routerLinkMatches[0]);

        var _button = /*#__PURE__*/React.createElement(CeleratisConnector.RouterLink, {
          to: params.to
        }, routerLinkMatches[1]);

        return /*#__PURE__*/React.createElement(Fragment, {
          key: key
        }, _strParts[0], _button, _strParts[1]);
      }

      return str;
    };

    if (string && params) {
      for (var param in params) {
        string = string.replace('%{' + param + '}', params[param]);
      }
    } else if (!string) {
      string = /*#__PURE__*/React.createElement("span", {
        className: "translationNotFound"
      }, name);
    } // replace content between asterisks with bolded text


    if (typeof string === 'string') {
      var stringParts = string.split('*');
      var numStringParts = stringParts.length;

      if (numStringParts >= 3 && numStringParts % 2 === 1) {
        string = stringParts.map(function (stringPart, i) {
          if (i % 2 === 0) {
            return parseURL(stringPart, i);
          }

          return /*#__PURE__*/React.createElement("strong", {
            key: i
          }, "  ", parseURL(stringPart, "".concat(i, "url")));
        });
      }
    }

    string = parseURL(string);
    return string;
  }
};

var World = function World(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24",
    height: "24",
    viewBox: "0 0 24 24"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M12,24A12,12,0,1,1,24,12,12.013,12.013,0,0,1,12,24ZM12,1.5A10.5,10.5,0,1,0,22.5,12,10.512,10.512,0,0,0,12,1.5Z"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M9.29,23.671a.749.749,0,0,1-.621-.327C7,20.893,6,16.652,6,12S7,3.108,8.669.657a.75.75,0,0,1,1.24.844C8.423,3.681,7.5,7.7,7.5,12s.923,8.32,2.409,10.5a.749.749,0,0,1-.2,1.042A.736.736,0,0,1,9.29,23.671Z"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M23.25,12.75H.75a.75.75,0,0,1,0-1.5h22.5a.75.75,0,0,1,0,1.5Z"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M21.518,18.75H2.482a.75.75,0,1,1,0-1.5H21.518a.75.75,0,0,1,0,1.5Z"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M21.518,6.75H2.482a.75.75,0,1,1,0-1.5H21.518a.75.75,0,0,1,0,1.5Z"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M14.71,23.671a.75.75,0,0,1-.618-1.173C15.577,20.32,16.5,16.3,16.5,12s-.923-8.32-2.408-10.5A.75.75,0,1,1,15.33.656C17,3.107,18,7.348,18,12s-1,8.894-2.67,11.344A.746.746,0,0,1,14.71,23.671Z"
  }));
};

var Favicon = function Favicon(_ref) {
  var faviconUrl = _ref.faviconUrl,
      siteUrl = _ref.siteUrl,
      className = _ref.className;

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      showDefault = _useState2[0],
      setShowDefault = _useState2[1];

  var imageSrc = faviconUrl ? faviconUrl : "https://www.google.com/s2/favicons?domain=".concat(siteUrl);
  var srcIsHttp = typeof imageSrc === 'string' && imageSrc.startsWith('http');
  var imageClasses = classNames(className, 'CeleratisFavicon'); // Set default image

  var image = /*#__PURE__*/React.createElement(World, {
    className: imageClasses
  }); // The favicon url must begin with http(s).
  // Prohibits use of other protocols, i.e. chrome://, chrome-extension://

  if (!showDefault && srcIsHttp) {
    image = /*#__PURE__*/React.createElement("img", {
      width: "16",
      height: "16",
      src: imageSrc,
      alt: I18n.t('site_icon_for', {
        string: imageSrc
      }),
      onError: function onError() {
        return setShowDefault(true);
      },
      className: imageClasses
    });
  }

  return image;
};

var Checkbox = function Checkbox(props) {
  return /*#__PURE__*/React.createElement("input", Object.assign({
    type: "checkbox"
  }, props, {
    className: classNames(props.className, 'CeleratisCheckbox')
  }));
};

var CheckboxWithLabel = function CheckboxWithLabel(props) {
  return /*#__PURE__*/React.createElement("label", {
    className: "CeleratisCheckboxWithLabel"
  }, /*#__PURE__*/React.createElement("input", Object.assign({
    type: "checkbox"
  }, props, {
    className: classNames(props.className, 'CeleratisCheckbox')
  })), props.label);
};

var ErrorMessage = function ErrorMessage(_ref) {
  var message = _ref.message;
  return /*#__PURE__*/React.createElement("div", {
    className: "CeleratisErrorMessage"
  }, message);
};

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose(source, excluded);
  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

var View = function View(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;

  var onKeyDown = function onKeyDown(e) {
    return e.key === 'Enter' && onClick();
  };

  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      onKeyDown: onKeyDown,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24.002",
    height: "15.001",
    viewBox: "0 0 24.002 15.001"
  }), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0)"
  }, /*#__PURE__*/React.createElement("g", {
    transform: "translate(-0.001 -4.499)"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M11.832,19.5C7.518,19.5,3.22,16.474.624,13.615a2.409,2.409,0,0,1,0-3.232C3.247,7.492,7.621,4.4,12,4.5c4.4-.085,8.753,2.992,11.379,5.879a2.411,2.411,0,0,1,0,3.233c-2.632,2.9-7.006,5.974-11.395,5.885ZM11.854,6C8.006,6,4.112,8.771,1.734,11.389a.912.912,0,0,0,0,1.22c2.4,2.647,6.346,5.455,10.251,5.389,3.913.073,7.874-2.742,10.283-5.391a.914.914,0,0,0,0-1.221C19.861,8.741,15.889,5.886,12.013,6h-.159Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(-0.001 -4.499)"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M12,16.5h0a4.5,4.5,0,0,1,0-9h0A4.505,4.505,0,0,1,16.5,12v0A4.506,4.506,0,0,1,12,16.5ZM12,9h0a3,3,0,1,0,0,6h0a3,3,0,0,0,3-3v0A3,3,0,0,0,12,9Zm3.749,3h0Z"
  }))));
};

var ViewNo = function ViewNo(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24.003",
    height: "19.5",
    viewBox: "0 0 24.003 19.5"
  }), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0 -2.25)"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M2.783,21.75a.75.75,0,0,1-.519-1.291l18.75-18a.75.75,0,0,1,1.039,1.082l-18.75,18A.748.748,0,0,1,2.783,21.75Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0 -2.25)"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M12.144,20.251h-.238a10.667,10.667,0,0,1-3.165-.483.75.75,0,0,1,.446-1.432,9.292,9.292,0,0,0,2.807.415c3.875.051,7.866-2.742,10.276-5.392a.912.912,0,0,0,0-1.219,19.846,19.846,0,0,0-2.959-2.653.75.75,0,0,1,.884-1.212,21.361,21.361,0,0,1,3.187,2.858,2.411,2.411,0,0,1,0,3.233C20.781,17.226,16.48,20.251,12.144,20.251Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0 -2.25)"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M3.778,17.011a.754.754,0,0,1-.459-.157A21.722,21.722,0,0,1,.625,14.367a2.408,2.408,0,0,1,0-3.234C3.252,8.24,7.669,5.185,12.012,5.25a9.9,9.9,0,0,1,2.583.3.75.75,0,1,1-.364,1.455,8.724,8.724,0,0,0-2.222-.26C8.065,6.674,4.141,9.492,1.734,12.139a.912.912,0,0,0,0,1.22,20.062,20.062,0,0,0,2.5,2.309.75.75,0,0,1-.461,1.343Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0 -2.25)"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M8.25,13.5a.75.75,0,0,1-.75-.75A4.5,4.5,0,0,1,12,8.25a.75.75,0,0,1,0,1.5,3,3,0,0,0-3,3A.75.75,0,0,1,8.25,13.5Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0 -2.25)"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M12,17.25a.75.75,0,0,1,0-1.5,3,3,0,0,0,3-3A.75.75,0,0,1,15.75,12h0a.75.75,0,0,1,.75.75,4.505,4.505,0,0,1-4.5,4.5Z"
  })));
};

// Good to use for a label's htmlFor and corresponding form control.

var idCount = 0;
function useUniqueId() {
  var prefix = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'uniqueId-';
  var id = useRef(idCount++);
  return prefix + id.current;
}

var Tooltip = function Tooltip(_ref) {
  var children = _ref.children,
      className = _ref.className,
      tooltipHeading = _ref.tooltipHeading,
      tooltipBody = _ref.tooltipBody,
      _ref$size = _ref.size,
      size = _ref$size === void 0 ? 'medium' : _ref$size,
      _ref$theme = _ref.theme,
      theme = _ref$theme === void 0 ? 'light' : _ref$theme,
      _ref$position = _ref.position,
      position = _ref$position === void 0 ? 'above' : _ref$position,
      _ref$tabIndex = _ref.tabIndex,
      tabIndex = _ref$tabIndex === void 0 ? 0 : _ref$tabIndex;
  var tooltipBodyId = useUniqueId('tooltip');
  var childrenWithAriaDescribedBy = Children.map(children, function (child, index) {
    if (index === 0 && isValidElement(child)) {
      return cloneElement(child, {
        'aria-describedby': tooltipBodyId,
        tabIndex: tabIndex
      });
    }

    return child;
  });
  return /*#__PURE__*/React.createElement("div", {
    className: classNames(className, 'CeleratisTooltipContainer')
  }, childrenWithAriaDescribedBy, /*#__PURE__*/React.createElement("div", {
    className: "CeleratisTooltip CeleratisTooltip--".concat(size, " CeleratisTooltip--").concat(theme, " CeleratisTooltip--").concat(position)
  }, tooltipHeading && /*#__PURE__*/React.createElement("strong", {
    className: "CeleratisTooltipHeading"
  }, tooltipHeading), tooltipBody && /*#__PURE__*/React.createElement("div", {
    className: "CeleratisTooltipBody",
    id: tooltipBodyId
  }, tooltipBody)));
};

var defaultTooltipProps = {
  tooltipHeading: I18n.t('password_requirements'),
  tooltipBody: /*#__PURE__*/React.createElement("section", null, /*#__PURE__*/React.createElement("h2", {
    className: "CeleratisTooltipListHeading"
  }, I18n.t('at_least_8_characters_including')), /*#__PURE__*/React.createElement("ul", {
    className: "CeleratisTooltipUnorderedList"
  }, /*#__PURE__*/React.createElement("li", null, I18n.t('an_uppercase_letter')), /*#__PURE__*/React.createElement("li", null, I18n.t('a_lowercase_letter')), /*#__PURE__*/React.createElement("li", null, I18n.t('a_number')), /*#__PURE__*/React.createElement("li", null, I18n.t('and_a_special_character')))),
  theme: 'dark',
  position: 'right',
  size: 'large'
};

var PasswordInput = function PasswordInput(_ref) {
  var isConfirm = _ref.isConfirm,
      useDefaultTooltip = _ref.useDefaultTooltip,
      defaultTooltipPosition = _ref.defaultTooltipPosition,
      containerClasses = _ref.containerClasses,
      placeholder = _ref.placeholder,
      className = _ref.className,
      name = _ref.name,
      onChange = _ref.onChange,
      onKeyPress = _ref.onKeyPress,
      value = _ref.value,
      errorMessage = _ref.errorMessage,
      _ref$tooltipProps = _ref.tooltipProps,
      tooltipProps = _ref$tooltipProps === void 0 ? useDefaultTooltip ? defaultTooltipProps : undefined : _ref$tooltipProps,
      otherInputAttributes = _objectWithoutProperties(_ref, ["isConfirm", "useDefaultTooltip", "defaultTooltipPosition", "containerClasses", "placeholder", "className", "name", "onChange", "onKeyPress", "value", "errorMessage", "tooltipProps"]);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      iconVisible = _useState2[0],
      setIconVisible = _useState2[1];

  var icon;
  var type = 'password';

  if (!isConfirm) {
    icon = /*#__PURE__*/React.createElement(LinkButton, {
      onClick: function onClick() {
        return setIconVisible(true);
      },
      className: "CeleratisPassword-icon"
    }, /*#__PURE__*/React.createElement(ViewNo, null));

    if (iconVisible) {
      icon = /*#__PURE__*/React.createElement(LinkButton, {
        onClick: function onClick() {
          return setIconVisible(false);
        },
        className: "CeleratisPassword-icon"
      }, /*#__PURE__*/React.createElement(View, null));
      type = 'text';
    }
  }

  var buildInputBox = function buildInputBox() {
    var inputAndIcon = /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement("input", Object.assign({
      placeholder: placeholder,
      className: classNames(className, 'CeleratisInput', {
        hasError: errorMessage
      }),
      name: name,
      onChange: onChange,
      onKeyPress: onKeyPress,
      value: value,
      type: type
    }, otherInputAttributes)), icon);

    if (tooltipProps) {
      if (useDefaultTooltip && defaultTooltipPosition) {
        tooltipProps.position = defaultTooltipPosition;
      }

      return /*#__PURE__*/React.createElement(Tooltip, Object.assign({}, tooltipProps, {
        className: classNames(tooltipProps.className, 'CeleratisPassword')
      }), inputAndIcon);
    }

    return inputAndIcon;
  };

  var inputBox = buildInputBox();
  return /*#__PURE__*/React.createElement("div", {
    className: classNames(containerClasses, {
      CeleratisPassword: !tooltipProps
    })
  }, inputBox, /*#__PURE__*/React.createElement(ErrorMessage, {
    message: errorMessage
  }));
};

var TextInput = function TextInput(_ref) {
  var containerClasses = _ref.containerClasses,
      className = _ref.className,
      errorMessage = _ref.errorMessage,
      tooltipProps = _ref.tooltipProps,
      otherInputAttributes = _objectWithoutProperties(_ref, ["containerClasses", "className", "errorMessage", "tooltipProps"]);

  var buildInput = function buildInput() {
    var inputElement = /*#__PURE__*/React.createElement("input", Object.assign({
      type: "text",
      className: classNames(className, 'CeleratisInput', {
        hasError: errorMessage
      })
    }, otherInputAttributes));

    if (tooltipProps) {
      return /*#__PURE__*/React.createElement(Tooltip, Object.assign({
        className: "CeleratisPassword"
      }, tooltipProps), inputElement);
    }

    return inputElement;
  };

  var input = buildInput();
  return /*#__PURE__*/React.createElement("div", {
    className: containerClasses
  }, input, /*#__PURE__*/React.createElement(ErrorMessage, {
    message: errorMessage
  }));
};

var LargeTooltip = function LargeTooltip(_ref) {
  var children = _ref.children,
      className = _ref.className,
      tooltipHeading = _ref.tooltipHeading,
      tooltipBody = _ref.tooltipBody,
      _ref$theme = _ref.theme,
      theme = _ref$theme === void 0 ? 'light' : _ref$theme,
      _ref$position = _ref.position,
      position = _ref$position === void 0 ? 'above' : _ref$position,
      _ref$tabIndex = _ref.tabIndex,
      tabIndex = _ref$tabIndex === void 0 ? 0 : _ref$tabIndex;
  return /*#__PURE__*/React.createElement(Tooltip, {
    size: "large",
    className: className,
    tooltipHeading: tooltipHeading,
    tooltipBody: tooltipBody,
    theme: theme,
    position: position,
    tabIndex: tabIndex
  }, children);
};

var MediumTooltip = function MediumTooltip(_ref) {
  var children = _ref.children,
      className = _ref.className,
      tooltipHeading = _ref.tooltipHeading,
      tooltipBody = _ref.tooltipBody,
      _ref$theme = _ref.theme,
      theme = _ref$theme === void 0 ? 'light' : _ref$theme,
      _ref$position = _ref.position,
      position = _ref$position === void 0 ? 'above' : _ref$position,
      _ref$tabIndex = _ref.tabIndex,
      tabIndex = _ref$tabIndex === void 0 ? 0 : _ref$tabIndex;
  return /*#__PURE__*/React.createElement(Tooltip, {
    size: "medium",
    className: className,
    tooltipHeading: tooltipHeading,
    tooltipBody: tooltipBody,
    theme: theme,
    position: position,
    tabIndex: tabIndex
  }, children);
};

var SmallTooltip = function SmallTooltip(_ref) {
  var children = _ref.children,
      className = _ref.className,
      tooltipHeading = _ref.tooltipHeading,
      tooltipBody = _ref.tooltipBody,
      _ref$theme = _ref.theme,
      theme = _ref$theme === void 0 ? 'light' : _ref$theme,
      _ref$position = _ref.position,
      position = _ref$position === void 0 ? 'above' : _ref$position,
      _ref$tabIndex = _ref.tabIndex,
      tabIndex = _ref$tabIndex === void 0 ? 0 : _ref$tabIndex;
  return /*#__PURE__*/React.createElement(Tooltip, {
    size: "small",
    className: className,
    tooltipHeading: tooltipHeading,
    tooltipBody: tooltipBody,
    theme: theme,
    position: position,
    tabIndex: tabIndex
  }, children);
};

var Header = function Header(_ref) {
  var children = _ref.children;
  return /*#__PURE__*/React.createElement("header", {
    className: "CeleratisHeader"
  }, children);
};

var CircleLoader = memo(function (_ref) {
  var className = _ref.className;
  return /*#__PURE__*/React.createElement("svg", {
    className: classNames('CeleratisCircleLoader', className),
    viewBox: "0 0 100 100"
  }, /*#__PURE__*/React.createElement("circle", {
    className: "CeleratisCircleLoader--background",
    fill: "transparent",
    cx: "50",
    cy: "50",
    r: "45"
  }), /*#__PURE__*/React.createElement("circle", {
    strokeLinecap: "round",
    fill: "transparent",
    className: "CeleratisCircleLoader--foreground",
    cx: "50",
    cy: "50",
    r: "45"
  }));
});

var Alert = function Alert(_ref) {
  var children = _ref.children,
      className = _ref.className;
  var classes = classNames(className, 'CeleratisAlert');
  return /*#__PURE__*/React.createElement("section", {
    className: classes
  }, children);
};

function _createForOfIteratorHelper(o) {
  if (typeof Symbol === "undefined" || o[Symbol.iterator] == null) {
    if (Array.isArray(o) || (o = _unsupportedIterableToArray(o))) {
      var i = 0;

      var F = function F() {};

      return {
        s: F,
        n: function n() {
          if (i >= o.length) return {
            done: true
          };
          return {
            done: false,
            value: o[i++]
          };
        },
        e: function e(_e) {
          throw _e;
        },
        f: F
      };
    }

    throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
  }

  var it,
      normalCompletion = true,
      didErr = false,
      err;
  return {
    s: function s() {
      it = o[Symbol.iterator]();
    },
    n: function n() {
      var step = it.next();
      normalCompletion = step.done;
      return step;
    },
    e: function e(_e2) {
      didErr = true;
      err = _e2;
    },
    f: function f() {
      try {
        if (!normalCompletion && it["return"] != null) it["return"]();
      } finally {
        if (didErr) throw err;
      }
    }
  };
}

var ToastNotification = function ToastNotification(_ref) {
  var _ref$notifications = _ref.notifications,
      notifications = _ref$notifications === void 0 ? [] : _ref$notifications;

  var _useState = useState({
    notifications: notifications
  }),
      _useState2 = _slicedToArray(_useState, 2),
      state = _useState2[0],
      setState = _useState2[1];

  var _useState3 = useState(),
      _useState4 = _slicedToArray(_useState3, 2),
      pauseTime = _useState4[0],
      setPauseTime = _useState4[1];

  var AUTO_DELETE_MS = 8000; // should match .CeleratisToastNotification {--animation-duration} in Notifications.scss

  var SLIDE_OUT_ANIMATION_MS = 400;
  var DISMISS_ANIMATION_MS = SLIDE_OUT_ANIMATION_MS / 2;
  var notificationRoot = document.getElementById('CeleratisToastNotificationRoot');

  if (!notificationRoot) {
    notificationRoot = document.createElement('div');
    notificationRoot.id = 'CeleratisToastNotificationRoot';
    document.body.appendChild(notificationRoot);
  }

  useEffect(function updateNotifications() {
    var _iterator = _createForOfIteratorHelper(notifications),
        _step;

    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var notification = _step.value;

        if (!notification.slideOutTime) {
          notification.slideOutTime = Date.now() + AUTO_DELETE_MS;
        }
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }

    setState({
      notifications: notifications
    });
  }, [notifications]);
  useEffect(function setAutoDeleteInterval() {
    function deleteNotification(id) {
      setState(function (s) {
        var _findNotification = findNotification(s.notifications, id),
            notification = _findNotification.notification,
            index = _findNotification.index;

        if (notification) {
          s.notifications.splice(index, 1);
          return {
            notifications: s.notifications
          };
        }
      });
    }

    function slideNotificationOut(id) {
      setState(function (s) {
        var _findNotification2 = findNotification(s.notifications, id),
            notification = _findNotification2.notification;

        if (notification) {
          notification.deleteTime = Date.now() + SLIDE_OUT_ANIMATION_MS;
          notification.slidOut = true;
          return {
            notifications: s.notifications
          };
        }
      });
    }

    var interval = setInterval(function () {
      var now = Date.now();

      var _iterator2 = _createForOfIteratorHelper(state.notifications),
          _step2;

      try {
        for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
          var notification = _step2.value;
          var deleteTime = notification.deleteTime,
              slideOutTime = notification.slideOutTime,
              slidOut = notification.slidOut;

          if (deleteTime && now >= deleteTime) {
            deleteNotification(notification.id);
          } else if (!pauseTime) {
            if (slideOutTime && now >= slideOutTime && !slidOut) {
              slideNotificationOut(notification.id);
            }
          }
        }
      } catch (err) {
        _iterator2.e(err);
      } finally {
        _iterator2.f();
      }
    }, 200);
    return function clearAutoDeleteInterval() {
      clearInterval(interval);
    };
  }, [state.notifications, pauseTime]);

  function findNotification(notifications, id) {
    var index = notifications.findIndex(function (notification) {
      return notification.id === id;
    });
    return {
      notification: notifications[index],
      index: index
    };
  }

  function dismissNotification(id) {
    setState(function (s) {
      var _findNotification3 = findNotification(s.notifications, id),
          notification = _findNotification3.notification;

      if (notification) {
        notification.deleteTime = Date.now() + DISMISS_ANIMATION_MS;
        notification.dismissed = true;
        return {
          notifications: s.notifications
        };
      }
    });
  }

  return createPortal( /*#__PURE__*/React.createElement("div", {
    className: "CeleratisToastNotificationContainer",
    onMouseEnter: function onMouseEnter() {
      return setPauseTime(Date.now());
    },
    onMouseLeave: function onMouseLeave() {
      return setPauseTime(undefined);
    }
  }, state.notifications.map(function (notification) {
    return /*#__PURE__*/React.createElement("div", {
      key: notification.id,
      className: classNames('CeleratisToastNotification', {
        SlideIn: !notification.slidOut && !notification.dismissed,
        SlideOut: notification.slidOut,
        Dismiss: notification.dismissed
      })
    }, /*#__PURE__*/React.createElement("button", {
      className: "CeleratisToastNotificationCloseButton",
      onClick: function onClick() {
        return dismissNotification(notification.id);
      }
    }, "\xD7"), /*#__PURE__*/React.createElement("div", {
      className: "CeleratisToastNotificationHeadingAndMessageContainer"
    }, /*#__PURE__*/React.createElement("strong", {
      className: "CeleratisToastNotificationHeading"
    }, notification.heading), /*#__PURE__*/React.createElement("div", {
      className: "CeleratisToastNotificationMessage"
    }, notification.message)));
  })), document.getElementById('CeleratisToastNotificationRoot'));
};

var Radio = function Radio(_ref) {
  var children = _ref.children,
      value = _ref.value,
      className = _ref.className,
      name = _ref.name,
      disabled = _ref.disabled,
      props = _objectWithoutProperties(_ref, ["children", "value", "className", "name", "disabled"]);

  var id = useUniqueId('radio');
  var labelRef = useRef();
  return /*#__PURE__*/React.createElement("div", {
    className: classNames(className, 'CeleratisRadio')
  }, /*#__PURE__*/React.createElement("input", Object.assign({
    id: id,
    type: "radio",
    name: name,
    value: value,
    className: "CeleratisRadioInput visually-hidden",
    disabled: disabled,
    onChange: function onChange() {}
  }, props)), /*#__PURE__*/React.createElement("label", {
    htmlFor: id,
    className: "CeleratisRadioLabel",
    ref: labelRef
  }, /*#__PURE__*/React.createElement("div", {
    className: "CeleratisRadioButton"
  }), children));
};

Radio.defaultProps = {
  type: 'Radio'
};

var RadioGroup = function RadioGroup(_ref) {
  var className = _ref.className,
      children = _ref.children,
      onChange = _ref.onChange,
      label = _ref.label,
      description = _ref.description,
      name = _ref.name,
      value = _ref.value;
  var labelId = useUniqueId('radioGroupLabel'); // if onChange is present, treat more like a controlled control.

  var checked = onChange ? 'checked' : 'defaultChecked';
  var newChildren = children.map(function (child) {
    var props = {};

    if (child.props.type === 'Radio') {
      props = _defineProperty({
        key: child.props.value,
        name: name
      }, checked, child.props.value === value);
    }

    return React.cloneElement(child, props);
  });
  return /*#__PURE__*/React.createElement("section", {
    className: className,
    onChange: onChange,
    role: "radiogroup",
    "aria-labelledby": labelId
  }, label && /*#__PURE__*/React.createElement("strong", {
    id: labelId
  }, label), description && /*#__PURE__*/React.createElement("p", {
    className: "color--grey600"
  }, description), /*#__PURE__*/React.createElement("div", {
    className: "m-top--8"
  }, newChildren));
};

var Select = function Select(_ref) {
  var defaultValue = _ref.defaultValue,
      _ref$options = _ref.options,
      options = _ref$options === void 0 ? [] : _ref$options,
      type = _ref.type,
      className = _ref.className,
      onChange = _ref.onChange,
      onBlur = _ref.onBlur,
      disabled = _ref.disabled;
  var classes = classNames(className, {
    CeleratisSelect: true,
    'CeleratisSelect--quiet': type === 'quiet'
  });
  return /*#__PURE__*/React.createElement("select", {
    className: classes,
    onChange: onChange,
    onBlur: onBlur,
    disabled: disabled,
    defaultValue: defaultValue
  }, options.map(function (item) {
    return /*#__PURE__*/React.createElement("option", {
      value: item.value,
      key: item.value
    }, item.label);
  }));
};

var QuietSelect = function QuietSelect(props) {
  return /*#__PURE__*/React.createElement(Select, Object.assign({}, props, {
    type: "quiet"
  }));
};

var uniqueKey = 0;

var GroupedTable = function GroupedTable(_ref) {
  var columnHeaders = _ref.columnHeaders,
      columnData = _ref.columnData,
      _ref$columnCount = _ref.columnCount,
      columnCount = _ref$columnCount === void 0 ? columnHeaders.length : _ref$columnCount;
  return /*#__PURE__*/React.createElement("table", {
    className: "CeleratisTable"
  }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, columnHeaders.map(function (th) {
    var text = th.label || th;
    return /*#__PURE__*/React.createElement("th", {
      key: uniqueKey++,
      className: classNames(th.className)
    }, text);
  }))), /*#__PURE__*/React.createElement("tbody", null, columnData.map(function (data) {
    return /*#__PURE__*/React.createElement(Fragment, {
      key: uniqueKey++
    }, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", {
      className: "CeleratisTable--grouped-labelCell",
      colSpan: columnCount
    }, data.label)), data.items && data.items.length > 0 && data.items.map(function (value) {
      return /*#__PURE__*/React.createElement("tr", {
        key: uniqueKey++
      }, /*#__PURE__*/React.createElement("td", null, value[0]), /*#__PURE__*/React.createElement("td", null, value[1]));
    }), data.customRow);
  })));
};

// All Tab data gets processed by the <Tabs> parent.

var Tab = function Tab() {
  return null;
};

Tab.defaultProps = {
  name: 'Tab'
};

var Tabs = function Tabs(_ref) {
  var children = _ref.children,
      containerClass = _ref.containerClass;
  // Construct an object with the all the tab info
  var allTabs = children.filter(function (child) {
    // Ensure we are mapping only over the Tab children
    return child.props.name === 'Tab';
  }).map(function (child) {
    return {
      label: child.props.label,
      content: child.props.children
    };
  });

  var _useState = useState(allTabs[0]),
      _useState2 = _slicedToArray(_useState, 2),
      selected = _useState2[0],
      setSelected = _useState2[1];

  return /*#__PURE__*/React.createElement("section", {
    className: containerClass
  }, /*#__PURE__*/React.createElement("ul", {
    className: "CeleratisTabList"
  }, allTabs.map(function (tab) {
    return /*#__PURE__*/React.createElement("li", {
      key: tab.label
    }, /*#__PURE__*/React.createElement("button", {
      className: classNames({
        CeleratisTabButton: true,
        'CeleratisTabButton--active': tab.label === selected.label
      }),
      onClick: function onClick() {
        return setSelected(tab);
      }
    }, tab.label));
  })), /*#__PURE__*/React.createElement("section", null, selected.content));
};

var ChevronRight = function ChevronRight(_ref) {
  var className = _ref.className;
  return /*#__PURE__*/React.createElement("svg", {
    className: className,
    width: "12.438",
    height: "24",
    viewBox: "0 0 12.438 24"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M23.78,6.781a.749.749,0,0,0-1.06,0L12,17.5,1.28,6.781A.75.75,0,0,0,.22,7.841l10.72,10.72A1.494,1.494,0,0,0,12,19h0a1.5,1.5,0,0,0,1.061-.438L23.78,7.841A.749.749,0,0,0,23.78,6.781Z",
    transform: "translate(-6.561 24) rotate(-90)"
  }));
};

var NextButton = function NextButton(_ref) {
  var onClick = _ref.onClick,
      _ref$disabled = _ref.disabled,
      disabled = _ref$disabled === void 0 ? false : _ref$disabled,
      className = _ref.className;
  return /*#__PURE__*/React.createElement("button", {
    className: classNames(className, 'CeleratisPaginationButton'),
    disabled: disabled,
    onClick: onClick
  }, /*#__PURE__*/React.createElement(ChevronRight, {
    className: "CeleratisPaginationIcon"
  }));
};

var ChevronLeft = function ChevronLeft(_ref) {
  var className = _ref.className;
  return /*#__PURE__*/React.createElement("svg", {
    className: className,
    width: "12.438",
    height: "24",
    viewBox: "0 0 12.438 24"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M23.78,6.781a.749.749,0,0,0-1.06,0L12,17.5,1.28,6.781A.75.75,0,0,0,.22,7.841l10.72,10.72A1.494,1.494,0,0,0,12,19h0a1.5,1.5,0,0,0,1.061-.438L23.78,7.841A.749.749,0,0,0,23.78,6.781Z",
    transform: "translate(18.999 0) rotate(90)"
  }));
};

var PreviousButton = function PreviousButton(_ref) {
  var onClick = _ref.onClick,
      _ref$disabled = _ref.disabled,
      disabled = _ref$disabled === void 0 ? false : _ref$disabled,
      className = _ref.className;
  return /*#__PURE__*/React.createElement("button", {
    className: classNames(className, 'CeleratisPaginationButton'),
    disabled: disabled,
    onClick: onClick
  }, /*#__PURE__*/React.createElement(ChevronLeft, {
    className: "CeleratisPaginationIcon"
  }));
};

function useEventListener(eventName, handler) {
  var element = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : window;
  var handlerRef = useRef();
  useEffect(function () {
    handlerRef.current = handler;
  }, [handler]);
  useEffect(function () {
    var listener = function listener(e) {
      return handlerRef.current(e);
    };

    element.addEventListener(eventName, listener);
    return function () {
      return element.removeEventListener(eventName, listener);
    };
  }, [eventName, element]);
}

var UserMenu = function UserMenu(_ref) {
  var userName = _ref.userName,
      viewProfile = _ref.viewProfile,
      className = _ref.className,
      buttonClick = _ref.buttonClick,
      signOutHandler = _ref.signOutHandler,
      feedbackHandler = _ref.feedbackHandler;

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      open = _useState2[0],
      setOpen = _useState2[1];

  var containerRef = useRef();
  var label = userName.charAt(0);

  var keyHandler = function keyHandler(_ref2) {
    var keyCode = _ref2.keyCode;
    return keyCode === 27 && setOpen(false);
  };

  var clickOutsideHandler = function clickOutsideHandler(_ref3) {
    var target = _ref3.target;
    return !containerRef.current.contains(target) && setOpen(false);
  };

  useEventListener('keydown', keyHandler);
  useEventListener('click', clickOutsideHandler);

  var buttonHandler = function buttonHandler(e) {
    buttonClick && buttonClick(e);
    setOpen(!open);
  };

  return /*#__PURE__*/React.createElement("div", {
    className: classNames('CeleratisUserMenu', className),
    ref: containerRef
  }, /*#__PURE__*/React.createElement("button", {
    onClick: function onClick(e) {
      return buttonHandler(e);
    },
    className: classNames(className, 'CeleratisUserMenuAvatar')
  }, /*#__PURE__*/React.createElement("span", {
    className: "CeleratisUserMenuAvatarLabel"
  }, label)), open && /*#__PURE__*/React.createElement("nav", {
    className: "CeleratisUserMenuContent"
  }, /*#__PURE__*/React.createElement("header", {
    className: "CeleratisUserMenuHeader"
  }, /*#__PURE__*/React.createElement("span", {
    className: "CeleratisUserMenuAvatar"
  }, /*#__PURE__*/React.createElement("span", {
    className: "CeleratisUserMenuAvatarLabel"
  }, label)), /*#__PURE__*/React.createElement("div", {
    className: "m-left--8"
  }, /*#__PURE__*/React.createElement("strong", null, userName), viewProfile && /*#__PURE__*/React.createElement("div", null, I18n.t('view_profile')))), /*#__PURE__*/React.createElement("ul", null, feedbackHandler && /*#__PURE__*/React.createElement("li", null, /*#__PURE__*/React.createElement("button", {
    className: "CeleratisUserMenuItem",
    onClick: function onClick(e) {
      return feedbackHandler(e);
    }
  }, I18n.t('give_feedback'))), /*#__PURE__*/React.createElement("li", null, /*#__PURE__*/React.createElement("a", {
    className: "CeleratisUserMenuItem",
    href: "https://help.relay.school",
    tabIndex: "0"
  }, I18n.t('get_help'))), /*#__PURE__*/React.createElement("li", null, /*#__PURE__*/React.createElement("a", {
    className: "CeleratisUserMenuItem",
    href: "https://www.lightspeedsystems.com/privacy/"
  }, I18n.t('privacy_policy'))), /*#__PURE__*/React.createElement("li", null, /*#__PURE__*/React.createElement("a", {
    className: "CeleratisUserMenuItem",
    href: "https://www.lightspeedsystems.com/tc/"
  }, I18n.t('terms_of_use'))), signOutHandler && /*#__PURE__*/React.createElement("li", null, /*#__PURE__*/React.createElement("button", {
    className: "CeleratisUserMenuSignOut CeleratisUserMenuItem",
    onClick: function onClick(e) {
      return signOutHandler(e);
    }
  }, I18n.t('sign_out'))))));
};

var Toggle = function Toggle(_ref) {
  var className = _ref.className,
      onChange = _ref.onChange,
      _onClick = _ref.onClick,
      disabled = _ref.disabled,
      show = _ref.show;

  function changeHandler(e) {
    setChecked(e.target.checked);
    onChange && onChange(e);
  }

  var _useState = useState(show),
      _useState2 = _slicedToArray(_useState, 2),
      checked = _useState2[0],
      setChecked = _useState2[1];

  var toggleClasses = classNames('CeleratisToggle', className, {
    'CeleratisToggle--checked': checked,
    'CeleratisToggle--disabled': disabled
  });
  useEffect(function () {
    setChecked(show);
  }, [show]);
  return /*#__PURE__*/React.createElement("label", {
    className: toggleClasses
  }, /*#__PURE__*/React.createElement("input", {
    type: "checkbox",
    className: "visually-hidden",
    onChange: function onChange(e) {
      return changeHandler(e);
    },
    onClick: function onClick(e) {
      return _onClick && _onClick(e);
    },
    disabled: disabled
  }));
};

function useMediaQuery(queries, // Array of media query strings. e.g. ['(min-width: 100px)', '(min-width: 200px)', '(min-width: 300px)']
values, // Array corresponding to index of the queries.
defaultValue) // Base value
{
  var mediaQueryLists = queries.map(function (query) {
    return window.matchMedia(query);
  });
  var getValue = useCallback(function () {
    var index = mediaQueryLists.findIndex(function (list) {
      return list.matches;
    });
    return typeof values[index] !== 'undefined' ? values[index] : defaultValue;
  }, [defaultValue, mediaQueryLists, values]);

  var _useState = useState(getValue),
      _useState2 = _slicedToArray(_useState, 2),
      value = _useState2[0],
      setValue = _useState2[1];

  useEffect(function () {
    var handler = function handler() {
      return setValue(getValue);
    };

    mediaQueryLists.forEach(function (lists) {
      return lists.addListener(handler);
    });
    return function () {
      return mediaQueryLists.forEach(function (lists) {
        return lists.removeListener(handler);
      });
    };
  }, [getValue, mediaQueryLists]);
  return value;
}

function useOnScreen(ref) {
  var rootMargin = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '0px';

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isIntersecting = _useState2[0],
      setIntersecting = _useState2[1];

  useEffect(function () {
    var observer = new IntersectionObserver(function (_ref) {
      var _ref2 = _slicedToArray(_ref, 1),
          entry = _ref2[0];

      setIntersecting(entry.isIntersecting);
    }, {
      rootMargin: rootMargin
    });

    if (ref.current) {
      observer.observe(ref.current);
    }

    return function () {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, []);
  return isIntersecting;
}

var MIN_PASSWORD_LENGTH = 8;
var passwordMeetsRequirements = function passwordMeetsRequirements(proposedPassword) {
  return Boolean(proposedPassword.length >= MIN_PASSWORD_LENGTH && proposedPassword.match(/[a-z]/) && proposedPassword.match(/[A-Z]/) && proposedPassword.match(/\d/) && proposedPassword.match(/[@$!%*?&.[{}()"#/,><':;|_~`^\\\]-]/) && proposedPassword.match(/^[\S]+.*[\S]+$/));
};
var passwordErrorsList = function passwordErrorsList(proposedPassword) {
  var errors = [];
  proposedPassword.length < MIN_PASSWORD_LENGTH && errors.push(I18n.t('at_least_8_characters'));
  !proposedPassword.match(/[a-z]/) && errors.push(I18n.t('contains_a_lowercase_letter'));
  !proposedPassword.match(/[A-Z]/) && errors.push(I18n.t('contains_an_uppercase_letter'));
  !proposedPassword.match(/\d/) && errors.push(I18n.t('contains_a_number'));
  !proposedPassword.match(/[@$!%*?&.[{}()"#/,><':;|_~`^\\\]-]/) && errors.push(I18n.t('contains_a_special_character'));
  !proposedPassword.match(/^[\S]+.*[\S]+$/) && errors.push(I18n.t('does_not_begin_or_end_with_blank_space'));

  if (errors.length === 0) {
    return null;
  }

  var errorListItems = errors.map(function (error) {
    return /*#__PURE__*/React.createElement("li", {
      key: error
    }, error);
  });
  return /*#__PURE__*/React.createElement("ul", {
    className: "CeleratisErrorList"
  }, errorListItems);
};

var Analytics = function Analytics(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24.001",
    height: "24",
    viewBox: "0 0 24.001 24"
  }), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0.001)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M22.5,24H.749a.75.75,0,1,1,0-1.5H22.5a.75.75,0,0,1,0,1.5Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0.001)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M6,24H2.249a.75.75,0,0,1-.75-.75v-4A1.252,1.252,0,0,1,2.749,18H5.5a1.252,1.252,0,0,1,1.25,1.25v4A.75.75,0,0,1,6,24ZM3,22.5H5.25v-3H3Zm2.5-3h0Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0.001)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M13.5,24H9.749A.75.75,0,0,1,9,23.25v-13A1.252,1.252,0,0,1,10.249,9H13a1.252,1.252,0,0,1,1.25,1.25v13A.75.75,0,0,1,13.5,24Zm-3-1.5H12.75v-12H10.5Zm2.5-12h0Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0.001)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M21,24H17.25a.75.75,0,0,1-.75-.75v-8.5a1.253,1.253,0,0,1,1.246-1.25H20.5a1.252,1.252,0,0,1,1.25,1.25v8.5A.75.75,0,0,1,21,24Zm-3-1.5h2.25V15H18ZM17.752,15h0Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0.001)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M2.866,9.884A.75.75,0,0,1,2.336,8.6L6.353,4.59a.75.75,0,0,1,1.06,1.062L3.4,9.665A.747.747,0,0,1,2.866,9.884Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0.001)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M14.311,7.075a.765.765,0,0,1-.194-.025L9.443,5.8a.75.75,0,0,1,.388-1.449L14.5,5.6a.75.75,0,0,1-.193,1.474Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0.001)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M16.812,6.439A.75.75,0,0,1,16.349,5.1L19.93,2.3a.75.75,0,0,1,.924,1.182l-3.582,2.8A.743.743,0,0,1,16.812,6.439Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0.001)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M8.249,6.751A2.25,2.25,0,1,1,10.5,4.5,2.252,2.252,0,0,1,8.249,6.751Zm0-3A.75.75,0,1,0,9,4.5.75.75,0,0,0,8.249,3.751Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0.001)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M21.75,4.5A2.25,2.25,0,1,1,24,2.25,2.253,2.253,0,0,1,21.75,4.5Zm0-3a.75.75,0,1,0,.75.75A.751.751,0,0,0,21.75,1.5Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0.001)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M15.75,9A2.25,2.25,0,1,1,18,6.75,2.253,2.253,0,0,1,15.75,9Zm0-3a.75.75,0,1,0,.75.75A.751.751,0,0,0,15.75,6Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0.001)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M2.249,12.751A2.25,2.25,0,1,1,4.5,10.5,2.252,2.252,0,0,1,2.249,12.751Zm0-3A.75.75,0,1,0,3,10.5.75.75,0,0,0,2.249,9.751Z"
  })));
};

var AppSwitch = function AppSwitch(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24",
    height: "24",
    viewBox: "0 0 24 24"
  }), /*#__PURE__*/React.createElement("g", {
    transform: "translate(-297.993 -378.75)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M307.362,380.4v7.719h-7.719V380.4h7.719m.547-1.65H299.1a1.1,1.1,0,0,0-1.1,1.1v8.813a1.1,1.1,0,0,0,1.1,1.1h8.813a1.1,1.1,0,0,0,1.1-1.1v-8.813a1.1,1.1,0,0,0-1.1-1.1Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(-297.993 -378.75)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M320.343,380.4v7.719h-7.719V380.4h7.719m.547-1.65h-8.813a1.1,1.1,0,0,0-1.1,1.1v8.813a1.1,1.1,0,0,0,1.1,1.1h8.813a1.1,1.1,0,0,0,1.1-1.1v-8.813a1.1,1.1,0,0,0-1.1-1.1Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(-297.993 -378.75)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M307.362,393.381V401.1h-7.719v-7.719h7.719m.547-1.65H299.1a1.1,1.1,0,0,0-1.1,1.1v8.813a1.1,1.1,0,0,0,1.1,1.1h8.813a1.1,1.1,0,0,0,1.1-1.1v-8.813a1.1,1.1,0,0,0-1.1-1.1Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(-297.993 -378.75)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M320.343,393.381V401.1h-7.719v-7.719h7.719m.547-1.65h-8.813a1.1,1.1,0,0,0-1.1,1.1v8.813a1.1,1.1,0,0,0,1.1,1.1h8.813a1.1,1.1,0,0,0,1.1-1.1v-8.813a1.1,1.1,0,0,0-1.1-1.1Z"
  })));
};

var ArrowDown = function ArrowDown(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "18.666",
    height: "24",
    viewBox: "0 0 18.666 24"
  }), /*#__PURE__*/React.createElement("g", {
    transform: "translate(25.417 27) rotate(180)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M12.584,27a1.334,1.334,0,0,1-1.333-1.333V4.333a1.333,1.333,0,1,1,2.667,0V25.667A1.334,1.334,0,0,1,12.584,27Z",
    transform: "translate(3.5 0)"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M24.084,13.666a1.323,1.323,0,0,1-.942-.391L16.084,6.219,9.026,13.275a1.333,1.333,0,1,1-1.884-1.884l8-8a1.332,1.332,0,0,1,1.884,0l8,8a1.333,1.333,0,0,1-.942,2.276Z",
    transform: "translate(0 0)"
  })));
};

var ArrowDownKPI = function ArrowDownKPI(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24",
    height: "24",
    viewBox: "0 0 24 24"
  }), /*#__PURE__*/React.createElement("g", {
    transform: "translate(24) rotate(90)"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M22.98,20.389h-5.1a1.02,1.02,0,0,1-1.02-1.02V12.8a.255.255,0,0,0-.434-.181L5.347,23.711a1.043,1.043,0,0,1-1.442,0L.3,20.1a1.02,1.02,0,0,1,0-1.441L11.376,7.576a.255.255,0,0,0-.18-.436H4.626a1.02,1.02,0,0,1-1.02-1.02V1.02A1.02,1.02,0,0,1,4.626,0H22.98A1.02,1.02,0,0,1,24,1.02V19.369A1.02,1.02,0,0,1,22.98,20.389Z",
    transform: "translate(0 0)"
  })));
};

var ArrowLeft = function ArrowLeft(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24",
    height: "18.666",
    viewBox: "0 0 24 18.666"
  }), /*#__PURE__*/React.createElement("g", {
    transform: "translate(-3 25.417) rotate(-90)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M12.584,27a1.334,1.334,0,0,1-1.333-1.333V4.333a1.333,1.333,0,1,1,2.667,0V25.667A1.334,1.334,0,0,1,12.584,27Z",
    transform: "translate(3.5 0)"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M24.084,13.666a1.323,1.323,0,0,1-.942-.391L16.084,6.219,9.026,13.275a1.333,1.333,0,1,1-1.884-1.884l8-8a1.332,1.332,0,0,1,1.884,0l8,8a1.333,1.333,0,0,1-.942,2.276Z",
    transform: "translate(0 0)"
  })));
};

var ArrowRight = function ArrowRight(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24",
    height: "18.666",
    viewBox: "0 0 24 18.666"
  }), /*#__PURE__*/React.createElement("g", {
    transform: "translate(27 -6.751) rotate(90)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M12.584,27a1.334,1.334,0,0,1-1.333-1.333V4.333a1.333,1.333,0,1,1,2.667,0V25.667A1.334,1.334,0,0,1,12.584,27Z",
    transform: "translate(3.5 0)"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M24.084,13.666a1.323,1.323,0,0,1-.942-.391L16.084,6.219,9.026,13.275a1.333,1.333,0,1,1-1.884-1.884l8-8a1.332,1.332,0,0,1,1.884,0l8,8a1.333,1.333,0,0,1-.942,2.276Z",
    transform: "translate(0 0)"
  })));
};

var ArrowUp = function ArrowUp(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "18.666",
    height: "24",
    viewBox: "0 0 18.666 24"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M12.584,27a1.334,1.334,0,0,1-1.333-1.333V4.333a1.333,1.333,0,1,1,2.667,0V25.667A1.334,1.334,0,0,1,12.584,27Z",
    transform: "translate(-3.251 -3)"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M24.084,13.666a1.323,1.323,0,0,1-.942-.391L16.084,6.219,9.026,13.275a1.333,1.333,0,1,1-1.884-1.884l8-8a1.332,1.332,0,0,1,1.884,0l8,8a1.333,1.333,0,0,1-.942,2.276Z",
    transform: "translate(-6.751 -3)"
  }));
};

var ArrowUpKPI = function ArrowUpKPI(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24",
    height: "24",
    viewBox: "0 0 24 24"
  }), /*#__PURE__*/React.createElement("g", {
    transform: "translate(-0.079 24.077) rotate(-90)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M23.057,3.689h-5.1a1.02,1.02,0,0,0-1.02,1.02v6.568a.255.255,0,0,1-.434.181L5.423.368a1.043,1.043,0,0,0-1.442,0L.375,3.976a1.02,1.02,0,0,0,0,1.441L11.453,16.5a.255.255,0,0,1-.18.436H4.7a1.02,1.02,0,0,0-1.02,1.02v5.1a1.02,1.02,0,0,0,1.02,1.02H23.057a1.02,1.02,0,0,0,1.02-1.02V4.709A1.02,1.02,0,0,0,23.057,3.689Z",
    transform: "translate(0)"
  })));
};

var Bell = function Bell(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "19.5",
    height: "24",
    viewBox: "0 0 19.5 24"
  }), /*#__PURE__*/React.createElement("g", {
    transform: "translate(-2.25)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M12.005,24A2.844,2.844,0,0,1,9.28,21.961a.75.75,0,0,1,1.44-.422,1.337,1.337,0,0,0,2.565,0,.75.75,0,0,1,1.44.422A2.837,2.837,0,0,1,12.005,24Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(-2.25)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M12,3.75A.75.75,0,0,1,11.25,3V.75a.75.75,0,0,1,1.5,0V3A.75.75,0,0,1,12,3.75Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(-2.25)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M21,19.5H3a.75.75,0,0,1-.59-1.212c.006-.009,1.34-1.9,1.34-7.788a8.25,8.25,0,0,1,16.5,0c0,6.293,1.207,7.655,1.22,7.665A.751.751,0,0,1,21,19.5ZM4.209,18H19.714c-.474-1.212-.964-3.446-.964-7.5a6.75,6.75,0,0,0-13.5,0A21.319,21.319,0,0,1,4.209,18Z"
  })));
};

var Bell$1 = function Bell(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "22.501",
    height: "22.501",
    viewBox: "0 0 22.501 22.501"
  }), /*#__PURE__*/React.createElement("g", {
    transform: "translate(-0.75 -0.749)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M4.344,7.487a.75.75,0,0,1-.656-1.114A7.089,7.089,0,0,1,6.45,3.61a.75.75,0,0,1,.728,1.312A5.592,5.592,0,0,0,5,7.1.749.749,0,0,1,4.344,7.487Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(-0.75 -0.749)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M1.5,6.726A.772.772,0,0,1,1.295,6.7a.752.752,0,0,1-.517-.927A7.2,7.2,0,0,1,5.77.779a.75.75,0,1,1,.41,1.442,5.713,5.713,0,0,0-3.958,3.96A.751.751,0,0,1,1.5,6.726Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(-0.75 -0.749)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M19.5,18.75H4.5a.75.75,0,0,1,0-1.5h15a.75.75,0,0,1,0,1.5Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(-0.75 -0.749)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M4.5,18.75a.75.75,0,0,1-.487-1.32h0S5.25,16.2,5.25,12c0-4.311,3.028-7.818,6.75-7.818S18.75,7.689,18.75,12c0,4.184,1.244,5.436,1.257,5.447a.759.759,0,0,1,.038,1.053.738.738,0,0,1-1.037.065c-.18-.156-1.758-1.67-1.758-6.565,0-3.484-2.355-6.318-5.25-6.318S6.75,8.516,6.75,12c0,4.923-1.583,6.416-1.763,6.57A.744.744,0,0,1,4.5,18.75Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(-0.75 -0.749)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M12,23.25A2.253,2.253,0,0,1,9.75,21a.75.75,0,0,1,1.5,0,.75.75,0,0,0,1.5,0,.75.75,0,0,1,1.5,0A2.253,2.253,0,0,1,12,23.25Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(-0.75 -0.749)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M12,5.682a.75.75,0,0,1-.75-.75V3a.75.75,0,0,1,1.5,0V4.932A.75.75,0,0,1,12,5.682Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(-0.75 -0.749)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M19.657,7.487A.75.75,0,0,1,19,7.1a5.592,5.592,0,0,0-.938-1.244,5.691,5.691,0,0,0-1.243-.937.75.75,0,1,1,.731-1.31,7.119,7.119,0,0,1,2.761,2.761.75.75,0,0,1-.291,1.02A.741.741,0,0,1,19.657,7.487Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(-0.75 -0.749)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M22.5,6.726a.751.751,0,0,1-.722-.545,5.711,5.711,0,0,0-3.958-3.96A.75.75,0,0,1,18.23.779a7.206,7.206,0,0,1,4.992,4.992A.751.751,0,0,1,22.7,6.7.767.767,0,0,1,22.5,6.726Z"
  })));
};

var BookOpen = function BookOpen(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24.239",
    height: "20.656",
    viewBox: "0 0 24.239 20.656"
  }), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0 -1.458)"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M11.995,22.112a2.329,2.329,0,0,1-.792-.138l-.02-.008a12.985,12.985,0,0,0-8.225-.422A2.357,2.357,0,0,1,0,19.261V4.928A2.349,2.349,0,0,1,1.645,2.685,14.627,14.627,0,0,1,11.732,2.9a.862.862,0,0,0,.558-.008,14.616,14.616,0,0,1,10.083-.2A2.344,2.344,0,0,1,24,4.926V19.262a2.356,2.356,0,0,1-2.941,2.286,13.011,13.011,0,0,0-8.24.418A2.406,2.406,0,0,1,11.995,22.112Zm-.273-1.546a.852.852,0,0,0,.566,0,14.451,14.451,0,0,1,9.157-.463.86.86,0,0,0,1.027-.622.882.882,0,0,0,.027-.216V4.928a.854.854,0,0,0-.6-.813,13.157,13.157,0,0,0-9.084.183,2.381,2.381,0,0,1-1.612.008,13.156,13.156,0,0,0-9.09-.2A.862.862,0,0,0,1.5,4.93V19.262a.851.851,0,0,0,.25.607.873.873,0,0,0,.821.226A14.5,14.5,0,0,1,11.722,20.566Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0 -1.458)"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M12,22.114a.75.75,0,0,1-.75-.75V3.682a.75.75,0,0,1,1.5,0V21.364A.75.75,0,0,1,12,22.114Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0 -1.458)"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M12.235,21.678a2.306,2.306,0,0,1-.792-.139l-.02-.008A12.992,12.992,0,0,0,3.2,21.109,2.357,2.357,0,0,1,.24,18.826V4.494A2.35,2.35,0,0,1,1.885,2.25a14.633,14.633,0,0,1,10.087.217.849.849,0,0,0,.558-.008,14.622,14.622,0,0,1,10.083-.2,2.347,2.347,0,0,1,1.626,2.236V18.827A2.356,2.356,0,0,1,21.3,21.113a13.007,13.007,0,0,0-8.239.418A2.389,2.389,0,0,1,12.235,21.678Zm-.273-1.546a.865.865,0,0,0,.566,0,14.447,14.447,0,0,1,9.158-.464.86.86,0,0,0,1.026-.622.83.83,0,0,0,.027-.216V4.494a.854.854,0,0,0-.6-.814,13.16,13.16,0,0,0-9.085.184,2.381,2.381,0,0,1-1.612.008,13.16,13.16,0,0,0-9.089-.2.861.861,0,0,0-.616.821V18.827a.857.857,0,0,0,.25.608.876.876,0,0,0,.822.225A14.491,14.491,0,0,1,11.962,20.132Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0 -1.458)"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M12.24,21.68a.75.75,0,0,1-.75-.75V3.248a.75.75,0,0,1,1.5,0V20.93A.75.75,0,0,1,12.24,21.68Z"
  })));
};

var Calendar = function Calendar(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24",
    height: "24",
    viewBox: "0 0 24 24"
  }), /*#__PURE__*/React.createElement("g", {
    transform: "translate(-0.002)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M21.752,24H2.252A2.253,2.253,0,0,1,0,21.75V5.25A2.252,2.252,0,0,1,2.252,3h19.5A2.253,2.253,0,0,1,24,5.25v16.5A2.253,2.253,0,0,1,21.752,24ZM2.252,4.5a.75.75,0,0,0-.75.75v16.5a.751.751,0,0,0,.75.75h19.5a.752.752,0,0,0,.75-.75V5.25a.751.751,0,0,0-.75-.75Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(-0.002)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M23.252,10.5H.752a.75.75,0,0,1,0-1.5h22.5a.75.75,0,0,1,0,1.5Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(-0.002)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M6.752,6.75A.75.75,0,0,1,6,6V.75a.75.75,0,0,1,1.5,0V6A.75.75,0,0,1,6.752,6.75Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(-0.002)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M17.252,6.75A.75.75,0,0,1,16.5,6V.75a.75.75,0,0,1,1.5,0V6A.75.75,0,0,1,17.252,6.75Z"
  })));
};

var CalendarAlt = function CalendarAlt(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24",
    height: "24",
    viewBox: "0 0 24 24"
  }), /*#__PURE__*/React.createElement("g", {
    transform: "translate(-0.002)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M21.752,24H2.252A2.253,2.253,0,0,1,0,21.75V5.25A2.252,2.252,0,0,1,2.252,3h19.5A2.253,2.253,0,0,1,24,5.25v16.5A2.253,2.253,0,0,1,21.752,24ZM2.252,4.5a.75.75,0,0,0-.75.75v16.5a.751.751,0,0,0,.75.75h19.5a.752.752,0,0,0,.75-.75V5.25a.751.751,0,0,0-.75-.75Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(-0.002)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M23.252,10.5H.752a.75.75,0,0,1,0-1.5h22.5a.75.75,0,0,1,0,1.5Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(-0.002)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M6.752,6.75A.75.75,0,0,1,6,6V.75a.75.75,0,0,1,1.5,0V6A.75.75,0,0,1,6.752,6.75Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(-0.002)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M17.252,6.75A.75.75,0,0,1,16.5,6V.75a.75.75,0,0,1,1.5,0V6A.75.75,0,0,1,17.252,6.75Z"
  })));
};

var Caution = function Caution(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24",
    height: "21",
    viewBox: "0 0 24 21"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M28.193,27.971l-10.019-17.1A1.91,1.91,0,0,0,16.46,9.9a2,2,0,0,0-1.714.968L4.727,27.971a1.875,1.875,0,0,0,0,1.961,1.91,1.91,0,0,0,1.714.968H26.479a1.949,1.949,0,0,0,1.714-2.929ZM16.091,11.6a.5.5,0,0,1,.369-.235.454.454,0,0,1,.4.235l9.993,17.1a.454.454,0,0,1,0,.445l-.026.026a.412.412,0,0,1-.369.209H6.441a.475.475,0,0,1-.4-.235.454.454,0,0,1,0-.445Z",
    transform: "translate(-4.45 -9.9)"
  }), /*#__PURE__*/React.createElement("rect", {
    fill: "currentColor",
    width: "1.503",
    height: "7.218",
    transform: "translate(11.245 7.479)"
  }), /*#__PURE__*/React.createElement("rect", {
    fill: "currentColor",
    width: "1.503",
    height: "1.804",
    transform: "translate(11.245 15.587)"
  }));
};

var CheckmarkAlt = function CheckmarkAlt(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24",
    height: "17.453",
    viewBox: "0 0 24 17.453"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M7.956,17.143.319,9.688a1.032,1.032,0,0,1,0-1.492L1.862,6.7A1.121,1.121,0,0,1,3.4,6.7l5.324,5.224L20.6.308a1.121,1.121,0,0,1,1.543,0L23.681,1.8a1.029,1.029,0,0,1,0,1.49L9.5,17.148A1.121,1.121,0,0,1,7.956,17.143Z"
  }));
};

var CheckmarkTall = function CheckmarkTall(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "27.372",
    height: "24",
    viewBox: "0 0 27.372 24"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M13.514,30.078a3.911,3.911,0,0,1-.718-.069,3.624,3.624,0,0,1-2.321-1.551L5.528,21.437a1.52,1.52,0,1,1,2.486-1.75l4.967,7.051a.639.639,0,0,0,.408.286.594.594,0,0,0,.454-.091l.164-.16,15.9-20.118a1.521,1.521,0,0,1,2.388,1.886l-15.9,20.118a3.555,3.555,0,0,1-.862.8A3.6,3.6,0,0,1,13.514,30.078Z",
    transform: "translate(-5.25 -6.078)"
  }));
};

var ChevronDown = function ChevronDown(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24",
    height: "12.438",
    viewBox: "0 0 24 12.438"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M23.78,6.781a.749.749,0,0,0-1.06,0L12,17.5,1.28,6.781A.75.75,0,0,0,.22,7.841l10.72,10.72A1.494,1.494,0,0,0,12,19h0a1.5,1.5,0,0,0,1.061-.438L23.78,7.841A.749.749,0,0,0,23.78,6.781Z",
    transform: "translate(0 -6.561)"
  }));
};

var ChevronThickDown = function ChevronThickDown(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24",
    height: "15.394",
    viewBox: "0 0 24 15.394"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M306,403.7l-11.3-11.3a2.4,2.4,0,0,1,3.394-3.394l7.9,7.9,7.9-7.9A2.4,2.4,0,0,1,317.3,392.4Z",
    transform: "translate(-294 -388.303)"
  }));
};

var ChevronThickLeft = function ChevronThickLeft(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "15.394",
    height: "24",
    viewBox: "0 0 15.394 24"
  }), /*#__PURE__*/React.createElement("g", {
    transform: "translate(403.697 -294) rotate(90)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M306,403.7l-11.3-11.3a2.4,2.4,0,0,1,3.394-3.394l7.9,7.9,7.9-7.9A2.4,2.4,0,0,1,317.3,392.4Z"
  })));
};

var ChevronThickLeft$1 = function ChevronThickLeft(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "15.394",
    height: "24",
    viewBox: "0 0 15.394 24"
  }), /*#__PURE__*/React.createElement("g", {
    transform: "translate(-388.303 318) rotate(-90)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M306,403.7l-11.3-11.3a2.4,2.4,0,0,1,3.394-3.394l7.9,7.9,7.9-7.9A2.4,2.4,0,0,1,317.3,392.4Z"
  })));
};

var ChevronThickUp = function ChevronThickUp(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24",
    height: "15.394",
    viewBox: "0 0 24 15.394"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M306,388.3l-11.3,11.3a2.4,2.4,0,0,0,3.394,3.394l7.9-7.9,7.9,7.9A2.4,2.4,0,0,0,317.3,399.6Z",
    transform: "translate(-294 -388.303)"
  }));
};

var ChevronThickUp$1 = function ChevronThickUp(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24",
    height: "12.438",
    viewBox: "0 0 24 12.438"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M23.78,6.781a.749.749,0,0,0-1.06,0L12,17.5,1.28,6.781A.75.75,0,0,0,.22,7.841l10.72,10.72A1.494,1.494,0,0,0,12,19h0a1.5,1.5,0,0,0,1.061-.438L23.78,7.841A.749.749,0,0,0,23.78,6.781Z",
    transform: "translate(24 18.999) rotate(180)"
  }));
};

var Clock = function Clock(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24",
    height: "24",
    viewBox: "0 0 24 24"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M12.75,24.75a12,12,0,1,1,12-12A12.014,12.014,0,0,1,12.75,24.75Zm0-22.4a10.4,10.4,0,1,0,10.4,10.4A10.412,10.412,0,0,0,12.75,2.35Z",
    transform: "translate(-0.75 -0.75)"
  }), /*#__PURE__*/React.createElement("g", {
    transform: "translate(11.2 7.2)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M12.05,13.1a.8.8,0,0,1-.8-.8v-4a.8.8,0,1,1,1.6,0v4A.8.8,0,0,1,12.05,13.1Z",
    transform: "translate(-11.25 -7.5)"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(11.2 11.2)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M17.049,17.851a.8.8,0,0,1-.566-.234l-5-5a.8.8,0,0,1,1.131-1.131l5,5a.8.8,0,0,1-.565,1.365Z",
    transform: "translate(-11.25 -11.25)"
  })));
};

var Close = function Close(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24",
    height: "24",
    viewBox: "0 0 24 24"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M.75,24a.75.75,0,0,1-.53-1.28L22.72.219a.75.75,0,1,1,1.06,1.06l-22.5,22.5A.744.744,0,0,1,.75,24Z",
    transform: "translate(0 0.001)"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M23.25,24a.744.744,0,0,1-.53-.22L.22,1.279A.75.75,0,0,1,1.28.219l22.5,22.5A.75.75,0,0,1,23.25,24Z",
    transform: "translate(0 0.001)"
  }));
};

var Close$1 = function Close(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24",
    height: "24",
    viewBox: "0 0 24 24"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M23.876,3.518a1.822,1.822,0,0,0,0-2.576h0a1.814,1.814,0,0,0-2.561,0l-8.9,8.9L3.518.949a1.822,1.822,0,0,0-2.576,0h0a1.814,1.814,0,0,0,0,2.561l8.9,8.9L.949,21.307a1.822,1.822,0,0,0,0,2.576h0a1.814,1.814,0,0,0,2.561,0l8.9-8.9,8.895,8.895a1.822,1.822,0,0,0,2.576,0h0a1.814,1.814,0,0,0,0-2.561l-8.9-8.9Z",
    transform: "translate(-0.412 -0.412)"
  }));
};

var Cog = function Cog(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "23.989",
    height: "23.986",
    viewBox: "0 0 23.989 23.986"
  }), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0)"
  }, /*#__PURE__*/React.createElement("g", {
    transform: "translate(-0.006 -0.005)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M8.154,23.991a2.71,2.71,0,0,1-2.7-2.833l.072-1.4a1.211,1.211,0,0,0-1.145-1.269,1.18,1.18,0,0,0-.119,0l-1.4.071A2.709,2.709,0,0,1,.01,15.993,2.7,2.7,0,0,1,.9,13.844L1.938,12.9a1.211,1.211,0,0,0,.087-1.708,1.081,1.081,0,0,0-.089-.09L.9,10.161A2.712,2.712,0,0,1,.709,6.336a2.683,2.683,0,0,1,2.136-.885l1.4.071A1.2,1.2,0,0,0,5.518,4.379c0-.042,0-.087,0-.131l-.067-1.4A2.709,2.709,0,0,1,10.161.9L11.1,1.935h0a1.194,1.194,0,0,0,.834.4,1.167,1.167,0,0,0,.871-.308c.033-.03.063-.061.092-.092L13.846.895a2.709,2.709,0,0,1,4.713,1.957l-.071,1.4a1.211,1.211,0,0,0,1.147,1.268.976.976,0,0,0,.121,0l1.4-.071A2.71,2.71,0,0,1,23.1,10.163l-1.043.94a1.211,1.211,0,0,0-.086,1.709c.026.028.058.06.091.091l1.037.935a2.71,2.71,0,0,1,.2,3.827,2.742,2.742,0,0,1-2.149.889l-1.4-.071a1.248,1.248,0,0,0-.872.31,1.2,1.2,0,0,0-.4.834c0,.044,0,.081,0,.12l.071,1.405a2.708,2.708,0,0,1-4.7,1.954L12.9,22.062a1.208,1.208,0,0,0-1.708-.087,1.129,1.129,0,0,0-.092.091L10.161,23.1a2.687,2.687,0,0,1-1.876.883C8.241,23.99,8.2,23.991,8.154,23.991ZM4.315,16.983c.048,0,.1,0,.146,0a2.711,2.711,0,0,1,2.56,2.844l-.072,1.4a1.209,1.209,0,0,0,2.1.865L10,21.055c.064-.07.127-.133.194-.194a2.709,2.709,0,0,1,3.826.195l.941,1.041a1.209,1.209,0,0,0,2.1-.872l-.071-1.4c0-.091,0-.186,0-.282a2.687,2.687,0,0,1,.89-1.867,2.718,2.718,0,0,1,1.954-.694l1.4.071a1.209,1.209,0,0,0,.872-2.1l-1.041-.939a2.638,2.638,0,0,1-.191-.19,2.714,2.714,0,0,1,.19-3.832L22.1,9.049a1.2,1.2,0,0,0,.4-.955,1.22,1.22,0,0,0-1.266-1.149l-1.4.071c-.091,0-.18,0-.265,0A2.713,2.713,0,0,1,16.99,4.175l.071-1.4a1.208,1.208,0,0,0-.4-.959,1.21,1.21,0,0,0-1.707.086l-.948,1.041c-.064.07-.13.136-.2.2a2.708,2.708,0,0,1-1.951.691A2.69,2.69,0,0,1,9.99,2.941L9.05,1.9a1.209,1.209,0,0,0-2.1.872l.067,1.4a2.634,2.634,0,0,1,0,.281A2.706,2.706,0,0,1,4.17,7.02l-1.4-.071a1.221,1.221,0,0,0-.95.395A1.209,1.209,0,0,0,1.9,9.05L2.945,10a2.536,2.536,0,0,1,.194.194,2.714,2.714,0,0,1-.195,3.827l-1.041.94a1.208,1.208,0,0,0,0,1.792,1.224,1.224,0,0,0,.872.309l1.4-.071C4.223,16.984,4.269,16.983,4.315,16.983Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(-0.006 -0.005)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M12,17.251A5.25,5.25,0,1,1,17.25,12,5.256,5.256,0,0,1,12,17.251Zm0-9A3.75,3.75,0,1,0,15.75,12,3.754,3.754,0,0,0,12,8.251Z"
  }))));
};

var Disallow = function Disallow(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24",
    height: "24",
    viewBox: "0 0 24 24"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M12,24A12,12,0,1,1,24,12,12.013,12.013,0,0,1,12,24ZM12,1.5A10.5,10.5,0,1,0,22.5,12,10.512,10.512,0,0,0,12,1.5Z"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M4.045,20.7a.75.75,0,0,1-.53-1.28l15.91-15.91a.75.75,0,0,1,1.06,1.06L4.575,20.485A.744.744,0,0,1,4.045,20.7Z"
  }));
};

var DownloadCloud = function DownloadCloud(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24",
    height: "24.002",
    viewBox: "0 0 24 24.002"
  }), /*#__PURE__*/React.createElement("g", {
    transform: "translate(11.253 13.502)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M12,24a.75.75,0,0,1-.749-.75v-9a.749.749,0,1,1,1.5,0v9A.75.75,0,0,1,12,24Z",
    transform: "translate(-11.25 -13.5)"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(7.506 18.752)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M12,24a.743.743,0,0,1-.53-.22L7.72,20.03a.749.749,0,1,1,1.059-1.06L12,22.189l3.217-3.219a.749.749,0,0,1,1.059,1.06l-3.747,3.75A.743.743,0,0,1,12,24Z",
    transform: "translate(-7.5 -18.75)"
  })), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M18.369,16.5H16.487a.75.75,0,0,1,0-1.5h1.881a4.125,4.125,0,0,0,.008-8.25h-.007a4.1,4.1,0,0,0-2.344.732A.749.749,0,0,1,14.862,7,6.745,6.745,0,1,0,1.6,9.495a6.787,6.787,0,0,0,5.929,5.468.75.75,0,0,1-.156,1.492A8.251,8.251,0,0,1,6.712.141a8.248,8.248,0,0,1,9.374,5.593,5.6,5.6,0,0,1,2.283-.484h.009a5.625,5.625,0,0,1-.009,11.25Z",
    transform: "translate(0.011 0.002)"
  }));
};

var Drawer = function Drawer(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24",
    height: "9",
    viewBox: "0 0 24 9"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M21.75,16.5H2.25A2.253,2.253,0,0,1,0,14.25V9.75A2.252,2.252,0,0,1,2.25,7.5H6.832a.75.75,0,0,1,.651.378,5.2,5.2,0,0,0,9.034,0,.75.75,0,0,1,.651-.378H21.75A2.253,2.253,0,0,1,24,9.75v4.5A2.253,2.253,0,0,1,21.75,16.5ZM2.25,9a.75.75,0,0,0-.75.75v4.5a.751.751,0,0,0,.75.75h19.5a.752.752,0,0,0,.75-.75V9.75A.751.751,0,0,0,21.75,9H17.587A6.7,6.7,0,0,1,6.414,9Z",
    transform: "translate(0 -7.5)"
  }));
};

var DrawerEmpty = function DrawerEmpty(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24",
    height: "15.517",
    viewBox: "0 0 24 15.517"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M26.038,18.4l-2.887-7a.837.837,0,0,0-.773-.532H6.019a.839.839,0,0,0-.773.531l-2.886,7a6.426,6.426,0,0,0-.167,1.233v4.5a2.253,2.253,0,0,0,2.25,2.25h19.5a2.253,2.253,0,0,0,2.25-2.25v-4.5A6.652,6.652,0,0,0,26.038,18.4ZM6.576,12.61H21.823l1.967,4.769H19.361a.75.75,0,0,0-.652.378,5.2,5.2,0,0,1-9.033,0,.749.749,0,0,0-.651-.378H4.61ZM24.693,24.129a.752.752,0,0,1-.75.75H4.443a.751.751,0,0,1-.75-.75v-4.5a.724.724,0,0,1,.077-.318c.008-.01.014-.023.022-.033a.742.742,0,0,1,.651-.4H8.606a6.7,6.7,0,0,0,11.174,0h4.163a.751.751,0,0,1,.75.75Z",
    transform: "translate(-2.193 -10.862)"
  }));
};

var Duplicate = function Duplicate(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24",
    height: "24",
    viewBox: "0 0 24 24"
  }), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0 0.004)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M21.75,21H5.25A2.253,2.253,0,0,1,3,18.746V2.246A2.252,2.252,0,0,1,5.25,0h16.5A2.253,2.253,0,0,1,24,2.246v16.5A2.253,2.253,0,0,1,21.75,21ZM5.25,1.5a.751.751,0,0,0-.75.75v16.5a.751.751,0,0,0,.75.75h16.5a.752.752,0,0,0,.75-.75V2.246a.752.752,0,0,0-.75-.75Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0 0.004)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M20.25,24h-18A2.253,2.253,0,0,1,0,21.746v-18a.75.75,0,0,1,1.5,0v18a.751.751,0,0,0,.75.75h18a.75.75,0,0,1,0,1.5Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0 0.004)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M14.25,15a.75.75,0,0,1-.75-.75v-9a.75.75,0,0,1,1.5,0v9A.75.75,0,0,1,14.25,15Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0 0.004)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M18.75,10.5h-9a.75.75,0,1,1,0-1.5h9a.75.75,0,1,1,0,1.5Z"
  })));
};

var Edit = function Edit(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24",
    height: "24",
    viewBox: "0 0 24 24"
  }), /*#__PURE__*/React.createElement("g", {
    transform: "translate(7.469)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M9.009,16.015a.815.815,0,0,1-.568-.229.764.764,0,0,1-.228-.66l.568-3.858a.773.773,0,0,1,.228-.44L19.245.912a3.293,3.293,0,0,1,4.549,0,3.046,3.046,0,0,1,0,4.406L13.558,15.235a.808.808,0,0,1-.455.22l-3.979.552A.866.866,0,0,1,9.009,16.015Zm3.98-1.331h0Zm-2.654-2.938-.379,2.571,2.653-.368L22.657,4.217a1.526,1.526,0,0,0,0-2.2,1.687,1.687,0,0,0-2.274,0Z",
    transform: "translate(-8.205 0)"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0 3.744)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M18.868,24.756H2.785A2.379,2.379,0,0,1,.372,22.419V6.837A2.378,2.378,0,0,1,2.785,4.5h8.042a.779.779,0,1,1,0,1.558H2.785a.792.792,0,0,0-.8.779V22.419a.793.793,0,0,0,.8.779H18.868a.794.794,0,0,0,.8-.779V14.628a.8.8,0,0,1,1.608,0v7.791A2.379,2.379,0,0,1,18.868,24.756Z",
    transform: "translate(-0.372 -4.5)"
  })));
};

var EmailSent = function EmailSent(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24",
    height: "24.003",
    viewBox: "0 0 24 24.003"
  }), /*#__PURE__*/React.createElement("g", {
    transform: "translate(3.75 1.5)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M16.447,21.394q-.365,0-.751-.027a.749.749,0,0,1-.7-.8.762.762,0,0,1,.8-.7,6.645,6.645,0,0,0,6.1-2.529,11.232,11.232,0,0,1-4.286-1.7c-.537-.324-1.074-.676-1.608-1.041a.751.751,0,0,1,.109-1.3,9.908,9.908,0,0,0,5.749-9.95,26.076,26.076,0,0,0-5.584,3.176c-.633.634-1.364,2.847-1.756,4.037-.124.373-.231.7-.32.942a.749.749,0,0,1-1.116.368C11.323,10.716,9.7,9.808,7.738,10.462a2.413,2.413,0,0,0-1.756,2.872.741.741,0,0,1-.02.4L5.541,15H6.75a.75.75,0,1,1,0,1.5H4.5a.75.75,0,0,1-.711-.987l.687-2.062A3.894,3.894,0,0,1,7.3,9.027c2.215-.74,4.034,0,5.794,1.078l0-.008c.65-1.971,1.3-3.81,2.12-4.627,1.584-1.584,6.765-3.816,6.985-3.91a.75.75,0,0,1,1.034.555c.015.084,1.362,7.987-5.336,11.945.161.1.323.2.484.3,3.222,1.934,4.549,1.459,4.561,1.457a.782.782,0,0,1,.816.163.746.746,0,0,1,.188.791A7.956,7.956,0,0,1,16.447,21.394Z",
    transform: "translate(-3.75 -1.5)"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0 15.002)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M12.752,24H.75a.75.75,0,0,1-.75-.75v-7.5A.75.75,0,0,1,.75,15h.8a.75.75,0,0,1,0,1.5H1.5v6H12v-6h-1.65a.75.75,0,0,1,0-1.5h2.4a.75.75,0,0,1,.75.75v7.5A.75.75,0,0,1,12.752,24Z",
    transform: "translate(0 -15)"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0 15.752)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M6.751,21a.746.746,0,0,1-.4-.114l-6-3.751a.75.75,0,1,1,.794-1.272l5.6,3.5,5.6-3.5a.75.75,0,0,1,.794,1.272l-6,3.751A.746.746,0,0,1,6.751,21Z",
    transform: "translate(0 -15.75)"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(7.501 11.251)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M8.625,13.5A1.125,1.125,0,1,1,9.75,12.375,1.127,1.127,0,0,1,8.625,13.5Zm0-1.5A.375.375,0,1,0,9,12.375.375.375,0,0,0,8.625,12Z",
    transform: "translate(-7.5 -11.25)"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(19.651 18.901)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M21.75,21.75a.744.744,0,0,1-.53-.22l-1.352-1.351a.75.75,0,1,1,1.061-1.061L22.28,20.47a.75.75,0,0,1-.53,1.28Z",
    transform: "translate(-19.648 -18.898)"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(16.846 19.846)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M19.5,23.251a.744.744,0,0,1-.53-.22l-1.907-1.907a.75.75,0,0,1,1.06-1.06l1.907,1.907a.75.75,0,0,1-.53,1.28Z",
    transform: "translate(-16.843 -19.843)"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(8.252)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M10.463,10.445a.75.75,0,0,1-.632-.346,10.193,10.193,0,0,1-1.58-4.8c0-.034,0-.068,0-.1A10.286,10.286,0,0,1,9.87.343a.75.75,0,0,1,1.163-.12,14.851,14.851,0,0,1,3.158,4.738.75.75,0,1,1-1.383.58,13.4,13.4,0,0,0-2.165-3.528,8.782,8.782,0,0,0-.891,3.239A8.675,8.675,0,0,0,11.1,9.29a.75.75,0,0,1-.632,1.154Z",
    transform: "translate(-8.25 0)"
  })));
};

var ExpandDown = function ExpandDown(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "22.792",
    height: "24",
    viewBox: "0 0 22.792 24"
  }), /*#__PURE__*/React.createElement("g", {
    transform: "translate(371.011 -750.781) rotate(90)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M22.583.209a.711.711,0,0,0-1.007,0L11.4,10.388,1.215.209A.712.712,0,0,0,.209,1.215L10.389,11.4a1.419,1.419,0,0,0,1.007.416h0A1.423,1.423,0,0,0,12.4,11.4L22.583,1.215A.711.711,0,0,0,22.583.209Z",
    transform: "translate(750.781 371.011) rotate(-90)"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M22.583.209a.711.711,0,0,0-1.007,0L11.4,10.388,1.215.209A.712.712,0,0,0,.209,1.215L10.389,11.4a1.419,1.419,0,0,0,1.007.416h0A1.423,1.423,0,0,0,12.4,11.4L22.583,1.215A.711.711,0,0,0,22.583.209Z",
    transform: "translate(762.969 371.011) rotate(-90)"
  })));
};

var ExpandLeft = function ExpandLeft(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24",
    height: "22.792",
    viewBox: "0 0 24 22.792"
  }), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0 0)"
  }, /*#__PURE__*/React.createElement("g", {
    transform: "translate(774.781 371.011) rotate(180)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M22.583.209a.711.711,0,0,0-1.007,0L11.4,10.388,1.215.209A.712.712,0,0,0,.209,1.215L10.389,11.4a1.419,1.419,0,0,0,1.007.416h0A1.423,1.423,0,0,0,12.4,11.4L22.583,1.215A.711.711,0,0,0,22.583.209Z",
    transform: "translate(750.781 371.011) rotate(-90)"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M22.583.209a.711.711,0,0,0-1.007,0L11.4,10.388,1.215.209A.712.712,0,0,0,.209,1.215L10.389,11.4a1.419,1.419,0,0,0,1.007.416h0A1.423,1.423,0,0,0,12.4,11.4L22.583,1.215A.711.711,0,0,0,22.583.209Z",
    transform: "translate(762.969 371.011) rotate(-90)"
  }))));
};

var ExpandRight = function ExpandRight(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24",
    height: "22.792",
    viewBox: "0 0 24 22.792"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M22.583.209a.711.711,0,0,0-1.007,0L11.4,10.388,1.215.209A.712.712,0,0,0,.209,1.215L10.389,11.4a1.419,1.419,0,0,0,1.007.416h0A1.423,1.423,0,0,0,12.4,11.4L22.583,1.215A.711.711,0,0,0,22.583.209Z",
    transform: "translate(0 22.792) rotate(-90)"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M22.583.209a.711.711,0,0,0-1.007,0L11.4,10.388,1.215.209A.712.712,0,0,0,.209,1.215L10.389,11.4a1.419,1.419,0,0,0,1.007.416h0A1.423,1.423,0,0,0,12.4,11.4L22.583,1.215A.711.711,0,0,0,22.583.209Z",
    transform: "translate(12.188 22.792) rotate(-90)"
  }));
};

var ExpandUp = function ExpandUp(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "22.792",
    height: "24",
    viewBox: "0 0 22.792 24"
  }), /*#__PURE__*/React.createElement("g", {
    transform: "translate(-348.219 774.781) rotate(-90)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M22.583.209a.711.711,0,0,0-1.007,0L11.4,10.388,1.215.209A.712.712,0,0,0,.209,1.215L10.389,11.4a1.419,1.419,0,0,0,1.007.416h0A1.423,1.423,0,0,0,12.4,11.4L22.583,1.215A.711.711,0,0,0,22.583.209Z",
    transform: "translate(750.781 371.011) rotate(-90)"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M22.583.209a.711.711,0,0,0-1.007,0L11.4,10.388,1.215.209A.712.712,0,0,0,.209,1.215L10.389,11.4a1.419,1.419,0,0,0,1.007.416h0A1.423,1.423,0,0,0,12.4,11.4L22.583,1.215A.711.711,0,0,0,22.583.209Z",
    transform: "translate(762.969 371.011) rotate(-90)"
  })));
};

var Export = function Export(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24",
    height: "18",
    viewBox: "0 0 24 18"
  }), /*#__PURE__*/React.createElement("g", {
    transform: "translate(-0.001 -3)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M12,16.5a.75.75,0,0,1-.75-.75v-12a.75.75,0,0,1,1.5,0v12A.75.75,0,0,1,12,16.5Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(-0.001 -3)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M16.5,9a.744.744,0,0,1-.53-.22L12,4.811,8.031,8.78a.75.75,0,1,1-1.06-1.06l4.5-4.5a.749.749,0,0,1,1.06,0l4.5,4.5A.75.75,0,0,1,16.5,9Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(-0.001 -3)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M20.251,21H3.751A3.755,3.755,0,0,1,0,17.25v-1.5a.75.75,0,0,1,1.5,0v1.5a2.253,2.253,0,0,0,2.25,2.25h16.5a2.253,2.253,0,0,0,2.25-2.25v-1.5a.75.75,0,0,1,1.5,0v1.5A3.755,3.755,0,0,1,20.251,21Z"
  })));
};

var ExternalLinkThick = function ExternalLinkThick(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24",
    height: "23.99",
    viewBox: "0 0 24 23.99"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M298.621,324.479l.075,0a1.353,1.353,0,0,0,.964-.4l7.515-7.515v3.149a1.365,1.365,0,1,0,2.73,0V313.27a1.369,1.369,0,0,0-1.363-1.365H302.1a1.365,1.365,0,1,0,0,2.73h3.143l-7.567,7.513a1.369,1.369,0,0,0,0,1.931A1.344,1.344,0,0,0,298.621,324.479Z",
    transform: "translate(-286.01 -311.905)"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M314.029,327.363h-.48v.092a1.366,1.366,0,0,0-.883,1.279v4.315a.1.1,0,0,1-.1.1H294.249a.1.1,0,0,1-.1-.1V314.73a.1.1,0,0,1,.1-.094h3.927a1.365,1.365,0,1,0,0-2.73h-3.927a2.843,2.843,0,0,0-2.842,2.84v18.307a2.845,2.845,0,0,0,2.842,2.841h18.315a2.845,2.845,0,0,0,2.844-2.841v-4.327A1.369,1.369,0,0,0,314.029,327.363Z",
    transform: "translate(-291.407 -311.904)"
  }));
};

var Filter = function Filter(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "23.951",
    height: "23.144",
    viewBox: "0 0 23.951 23.144"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M10.474,23.648a1.5,1.5,0,0,1-1.5-1.5V12.87A12.047,12.047,0,0,1,0,2.108C0,2.09,0,2.022,0,2A1.5,1.5,0,0,1,1.5.5H22.452a1.521,1.521,0,0,1,1.5,1.6v0A12.044,12.044,0,0,1,14.974,12.87v6.779a1.487,1.487,0,0,1-.541,1.153l-3,2.5A1.5,1.5,0,0,1,10.474,23.648ZM1.5,2a10.538,10.538,0,0,0,8.375,9.535.749.749,0,0,1,.6.735v9.875l3-2.5V12.274a.75.75,0,0,1,.6-.735,10.54,10.54,0,0,0,8.378-9.534Zm21.7.05h0ZM22.452,2Z",
    transform: "translate(0 -0.504)"
  }));
};

var FilterPage = function FilterPage(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "23.997",
    height: "24",
    viewBox: "0 0 23.997 24"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M21.747,24H8.247A2.253,2.253,0,0,1,6,21.75v-6a.75.75,0,0,1,1.5,0v6a.751.751,0,0,0,.75.75h13.5a.752.752,0,0,0,.75-.75v-15a.751.751,0,0,0-.75-.75h-7.5a.75.75,0,0,1,0-1.5h7.5A2.253,2.253,0,0,1,24,6.75v15A2.253,2.253,0,0,1,21.747,24Z"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M20.25,10.5h-7.5a.75.75,0,0,1,0-1.5h7.5a.75.75,0,0,1,0,1.5Z"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M20.25,15H10.5a.75.75,0,0,1,0-1.5H20.25a.75.75,0,0,1,0,1.5Z"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M20.25,19.5H10.5a.75.75,0,0,1,0-1.5h9.75a.75.75,0,0,1,0,1.5Z"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M5.25,13.5a.75.75,0,0,1-.75-.75V7.061L.531,3.091A1.81,1.81,0,0,1,1.811,0h9.878A1.811,1.811,0,0,1,12.97,3.091L9,7.061V9.75a.747.747,0,0,1-.22.53l-3,3A.746.746,0,0,1,5.25,13.5ZM1.811,1.5a.31.31,0,0,0-.22.091.311.311,0,0,0,0,.44L5.78,6.22A.747.747,0,0,1,6,6.75v4.189l1.5-1.5V6.75a.747.747,0,0,1,.22-.53l4.189-4.189a.311.311,0,0,0-.22-.531ZM8.25,9.75h0Z"
  }));
};

var Folder = function Folder(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24",
    height: "21",
    viewBox: "0 0 24 21"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M21.75,22.5H2.25A2.253,2.253,0,0,1,0,20.25V3.75A2.252,2.252,0,0,1,2.25,1.5H7.5a2.261,2.261,0,0,1,1.8.9l1.575,2.1H21.75A2.253,2.253,0,0,1,24,6.75v13.5A2.253,2.253,0,0,1,21.75,22.5ZM2.25,3a.75.75,0,0,0-.75.75v16.5a.751.751,0,0,0,.75.75h19.5a.752.752,0,0,0,.75-.75V6.75A.751.751,0,0,0,21.75,6H10.5a.75.75,0,0,1-.6-.3L8.1,3.3A.753.753,0,0,0,7.5,3Z",
    transform: "translate(0 -1.5)"
  }));
};

var FolderDirectory = function FolderDirectory(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "19.73",
    height: "24",
    viewBox: "0 0 19.73 24"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M12.175,13.143H22.936a1.755,1.755,0,0,0,1.794-1.714v-6a1.755,1.755,0,0,0-1.794-1.714H18.721L16.255,2.146A.926.926,0,0,0,15.762,2H12.175a1.755,1.755,0,0,0-1.794,1.714V7.143H6.794V2.857A.9.9,0,0,0,5,2.857V20a.878.878,0,0,0,.9.857h4.484v3.429A1.755,1.755,0,0,0,12.175,26H22.936a1.755,1.755,0,0,0,1.794-1.714v-6a1.755,1.755,0,0,0-1.794-1.714H18.721L16.255,15a.926.926,0,0,0-.493-.146H12.175a1.755,1.755,0,0,0-1.794,1.714v2.571H6.794V8.857h3.587v2.571A1.755,1.755,0,0,0,12.175,13.143Zm0,3.429h3.318l2.466,1.569a.926.926,0,0,0,.493.146h4.484v6H12.175Zm0-12.857h3.318l2.466,1.569a.926.926,0,0,0,.493.146h4.484v6H12.175Z",
    transform: "translate(-5 -2)"
  }));
};

var FolderOpen = function FolderOpen(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24.001",
    height: "21",
    viewBox: "0 0 24.001 21"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M19.55,22.5H2.447a.776.776,0,0,1-.223-.033A2.456,2.456,0,0,1,0,20.061V3.75A2.252,2.252,0,0,1,2.25,1.5h4.5A2.252,2.252,0,0,1,9,3.75V4.5h9.75A2.253,2.253,0,0,1,21,6.75V9h.751A2.253,2.253,0,0,1,24,11.249a2.281,2.281,0,0,1-.075.578l-2.2,8.986A2.258,2.258,0,0,1,19.55,22.5ZM4.694,21H19.55a.75.75,0,0,0,.725-.557l2.2-8.985a.835.835,0,0,0,.029-.208.751.751,0,0,0-.75-.75H7.878a.753.753,0,0,0-.721.541L4.814,20.666A2.367,2.367,0,0,1,4.694,21ZM2.25,3a.75.75,0,0,0-.75.75v16.3a.927.927,0,0,0,.963.927.955.955,0,0,0,.9-.692l2.344-9.631A2.271,2.271,0,0,1,7.877,9H19.5V6.75A.751.751,0,0,0,18.75,6H8.25a.75.75,0,0,1-.75-.75V3.75A.75.75,0,0,0,6.75,3Z",
    transform: "translate(0 -1.5)"
  }));
};

var Hamburger = function Hamburger(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "20",
    height: "13.5",
    viewBox: "0 0 20 13.5"
  }), /*#__PURE__*/React.createElement("g", {
    transform: "translate(-1.5 -5.253)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M20.786,18.753H2.214a.751.751,0,0,1,0-1.5H20.786a.751.751,0,0,1,0,1.5Z",
    transform: "translate(0)"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(-1.5 -5.253)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M20.786,12.753H2.214a.751.751,0,0,1,0-1.5H20.786a.751.751,0,0,1,0,1.5Z",
    transform: "translate(0)"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(-1.5 -5.253)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M20.786,6.753H2.214a.751.751,0,0,1,0-1.5H20.786a.751.751,0,0,1,0,1.5Z",
    transform: "translate(0)"
  })));
};

var Hamburger$1 = function Hamburger(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24",
    height: "14.769",
    viewBox: "0 0 24 14.769"
  }), /*#__PURE__*/React.createElement("g", {
    transform: "translate(3557 -1727) rotate(90)"
  }, /*#__PURE__*/React.createElement("g", {
    transform: "translate(1727 3533)"
  }, /*#__PURE__*/React.createElement("circle", {
    fill: "currentColor",
    cx: "2.769",
    cy: "2.769",
    r: "2.769",
    transform: "translate(9.231 0)"
  }), /*#__PURE__*/React.createElement("circle", {
    fill: "currentColor",
    cx: "2.769",
    cy: "2.769",
    r: "2.769",
    transform: "translate(0 0)"
  }), /*#__PURE__*/React.createElement("circle", {
    fill: "currentColor",
    cx: "2.769",
    cy: "2.769",
    r: "2.769",
    transform: "translate(9.231 9.231)"
  }), /*#__PURE__*/React.createElement("circle", {
    fill: "currentColor",
    cx: "2.769",
    cy: "2.769",
    r: "2.769",
    transform: "translate(0 9.231)"
  }), /*#__PURE__*/React.createElement("circle", {
    fill: "currentColor",
    cx: "2.769",
    cy: "2.769",
    r: "2.769",
    transform: "translate(9.231 18.462)"
  }), /*#__PURE__*/React.createElement("circle", {
    fill: "currentColor",
    cx: "2.769",
    cy: "2.769",
    r: "2.769",
    transform: "translate(0 18.462)"
  }))));
};

var HandleVertical = function HandleVertical(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "14.769",
    height: "24",
    viewBox: "0 0 14.769 24"
  }), /*#__PURE__*/React.createElement("circle", {
    fill: "currentColor",
    cx: "2.769",
    cy: "2.769",
    r: "2.769",
    transform: "translate(9.231)"
  }), /*#__PURE__*/React.createElement("circle", {
    fill: "currentColor",
    cx: "2.769",
    cy: "2.769",
    r: "2.769"
  }), /*#__PURE__*/React.createElement("circle", {
    fill: "currentColor",
    cx: "2.769",
    cy: "2.769",
    r: "2.769",
    transform: "translate(9.231 9.231)"
  }), /*#__PURE__*/React.createElement("circle", {
    fill: "currentColor",
    cx: "2.769",
    cy: "2.769",
    r: "2.769",
    transform: "translate(0 9.231)"
  }), /*#__PURE__*/React.createElement("circle", {
    fill: "currentColor",
    cx: "2.769",
    cy: "2.769",
    r: "2.769",
    transform: "translate(9.231 18.462)"
  }), /*#__PURE__*/React.createElement("circle", {
    fill: "currentColor",
    cx: "2.769",
    cy: "2.769",
    r: "2.769",
    transform: "translate(0 18.462)"
  }));
};

var Help = function Help(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24",
    height: "24",
    viewBox: "0 0 24 24"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M12,15a.75.75,0,0,1-.75-.75V13.244a2.253,2.253,0,0,1,1.5-2.122A2.25,2.25,0,1,0,9.75,9,.75.75,0,0,1,9,9.75H9A.75.75,0,0,1,8.25,9a3.75,3.75,0,1,1,5,3.536.752.752,0,0,0-.5.707V14.25A.75.75,0,0,1,12,15Z"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M12,18.75a1.125,1.125,0,1,1,1.125-1.125A1.127,1.127,0,0,1,12,18.75Z"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M12,24A12,12,0,1,1,24,12,12.013,12.013,0,0,1,12,24ZM12,1.5A10.5,10.5,0,1,0,22.5,12,10.512,10.512,0,0,0,12,1.5Z"
  }));
};

var Hierarchy = function Hierarchy(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24",
    height: "24",
    viewBox: "0 0 24 24"
  }), /*#__PURE__*/React.createElement("g", {
    transform: "translate(2.4 11.995)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M21.4,19.5a.779.779,0,0,1-.8-.716A5.8,5.8,0,0,0,14.452,13.5h-3.67A5.8,5.8,0,0,0,4.6,18.782a.747.747,0,0,1-.835.715A.769.769,0,0,1,3,18.714,7.359,7.359,0,0,1,10.814,12h3.6a7.363,7.363,0,0,1,7.78,6.716.769.769,0,0,1-.763.783Z",
    transform: "translate(-3 -11.993)"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(11.2 6)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M12.05,19.5a.776.776,0,0,1-.8-.75v-12a.8.8,0,0,1,1.6,0v12A.776.776,0,0,1,12.05,19.5Z",
    transform: "translate(-11.25 -5.998)"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(8)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M12.25,7.5a3.885,3.885,0,0,1-4-3.75,3.885,3.885,0,0,1,4-3.75,3.886,3.886,0,0,1,4,3.75A3.886,3.886,0,0,1,12.25,7.5Zm0-6a2.331,2.331,0,0,0-2.4,2.25A2.331,2.331,0,0,0,12.25,6a2.331,2.331,0,0,0,2.4-2.25A2.331,2.331,0,0,0,12.25,1.5Z",
    transform: "translate(-8.25 0.002)"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0 18)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M3.95,24a3.109,3.109,0,0,1-3.2-3,3.109,3.109,0,0,1,3.2-3,3.109,3.109,0,0,1,3.2,3A3.109,3.109,0,0,1,3.95,24Zm0-4.5a1.5,1.5,0,1,0,0,3,1.5,1.5,0,1,0,0-3Z",
    transform: "translate(-0.75 -17.998)"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(8.8 18)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M12.2,24A3.109,3.109,0,0,1,9,21a3.109,3.109,0,0,1,3.2-3,3.109,3.109,0,0,1,3.2,3A3.109,3.109,0,0,1,12.2,24Zm0-4.5A1.5,1.5,0,1,0,13.8,21,1.554,1.554,0,0,0,12.2,19.5Z",
    transform: "translate(-9 -17.998)"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(17.6 18)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M20.45,24a3.109,3.109,0,0,1-3.2-3,3.207,3.207,0,0,1,6.4,0A3.109,3.109,0,0,1,20.45,24Zm0-4.5a1.5,1.5,0,1,0,1.6,1.5A1.554,1.554,0,0,0,20.45,19.5Z",
    transform: "translate(-17.25 -17.998)"
  })));
};

var Home = function Home(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "23.78",
    height: "21.877",
    viewBox: "0 0 23.78 21.877"
  }), /*#__PURE__*/React.createElement("g", {
    transform: "translate(-0.003 -1.067)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M20.253,22.944h-6a.75.75,0,0,1-.75-.75v-6a.752.752,0,0,0-.75-.75h-1.5a.751.751,0,0,0-.75.75v6a.75.75,0,0,1-.75.75h-6a.75.75,0,0,1-.75-.75v-8.25a.75.75,0,1,1,1.5,0v7.5H9v-5.25a2.253,2.253,0,0,1,2.25-2.25h1.5A2.253,2.253,0,0,1,15,16.194v5.25h4.5v-7.5a.75.75,0,0,1,1.5,0v8.25A.75.75,0,0,1,20.253,22.944Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(-0.003 -1.067)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M.753,13.194a.75.75,0,0,1-.53-1.28L10.412,1.725a2.253,2.253,0,0,1,3.182,0l10.189,10.19-1.06,1.06L12.534,2.785a.753.753,0,0,0-1.062,0L1.283,12.974A.744.744,0,0,1,.753,13.194Z"
  })));
};

var Image = function Image(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24",
    height: "19.454",
    viewBox: "0 0 24 19.454"
  }), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0 -2.273)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M7.08,11.125A2.625,2.625,0,1,1,9.7,8.5,2.629,2.629,0,0,1,7.08,11.125Zm0-3.75A1.125,1.125,0,1,0,8.2,8.5,1.127,1.127,0,0,0,7.08,7.375Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0 -2.273)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M6.706,17.724a.75.75,0,0,1-.624-1.166l2.865-4.3c.011-.018.069-.1.083-.113a1.6,1.6,0,0,1,2.239-.248l.765.616,1.785-2.557a1.615,1.615,0,0,1,.421-.412,1.6,1.6,0,0,1,2.212.439l4.372,6.556a.75.75,0,1,1-1.248.832L15.2,10.816a.107.107,0,0,0-.13-.025l-2.269,3.238a.749.749,0,0,1-.509.313.76.76,0,0,1-.576-.157l-1.39-1.118a.1.1,0,0,0-.129.016L7.33,17.39A.75.75,0,0,1,6.706,17.724Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0 -2.273)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M21.75,21.727H2.25A2.253,2.253,0,0,1,0,19.477V4.523a2.252,2.252,0,0,1,2.25-2.25H18.867a2.264,2.264,0,0,1,1.611.678l2.883,2.955A2.242,2.242,0,0,1,24,7.477v12A2.253,2.253,0,0,1,21.75,21.727ZM2.25,3.773a.75.75,0,0,0-.75.75V19.477a.751.751,0,0,0,.75.75h19.5a.752.752,0,0,0,.75-.75v-12a.742.742,0,0,0-.213-.523L19.4,4a.745.745,0,0,0-.537-.226Z"
  })));
};

var Info = function Info(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24",
    height: "24",
    viewBox: "0 0 24 24"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M14.25,17.25H13.5A2.253,2.253,0,0,1,11.25,15V11.25H10.5a.75.75,0,0,1,0-1.5h.75a1.5,1.5,0,0,1,1.5,1.5V15a.752.752,0,0,0,.75.75h.75a.75.75,0,0,1,0,1.5Z"
  }), /*#__PURE__*/React.createElement("circle", {
    fill: "currentColor",
    cx: "1.125",
    cy: "1.125",
    r: "1.125",
    transform: "translate(10.5 6)"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M12,24A12,12,0,1,1,24,12,12.013,12.013,0,0,1,12,24ZM12,1.5A10.5,10.5,0,1,0,22.5,12,10.512,10.512,0,0,0,12,1.5Z"
  }));
};

var Keys = function Keys(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "23.982",
    height: "24.004",
    viewBox: "0 0 23.982 24.004"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M6.75,24a2.253,2.253,0,0,1-2.25-2.25V16.173A6.75,6.75,0,0,1,6.749,3.059h0A6.75,6.75,0,0,1,13.5,9.807h0A6.75,6.75,0,0,1,9,16.173v5.581A2.253,2.253,0,0,1,6.75,24Zm0-19.445h0A5.25,5.25,0,0,0,5.438,14.892.75.75,0,0,1,6,15.618v6.136a.75.75,0,0,0,1.5,0V15.618a.75.75,0,0,1,.563-.726A5.25,5.25,0,0,0,6.75,4.559Z"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M21.729,21.055a2.233,2.233,0,0,1-1.561-.633L15.645,15.9a6.7,6.7,0,0,1-1.979.594.75.75,0,0,1-.2-1.486,5.188,5.188,0,0,0,1.94-.676.749.749,0,0,1,.912.115l4.907,4.907a.621.621,0,0,0,.524.2.739.739,0,0,0,.526-.229.749.749,0,0,0,0-1.043l-4.9-4.895a.749.749,0,0,1-.115-.912,5.251,5.251,0,0,0-7.14-7.22.75.75,0,0,1-.748-1.3,6.75,6.75,0,0,1,9.456,8.763l4.517,4.513a2.251,2.251,0,0,1-1.569,3.823Z"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M6.75,11.307A2.25,2.25,0,1,1,9,9.057,2.252,2.252,0,0,1,6.75,11.307Zm0-3a.75.75,0,1,0,.75.75A.751.751,0,0,0,6.75,8.307Z"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M6.75,8.307A.75.75,0,0,1,6,7.557V3a3,3,0,0,1,6,0v.989a.75.75,0,0,1-1.5,0V3a1.5,1.5,0,0,0-3,0V7.557A.75.75,0,0,1,6.75,8.307Z"
  }));
};

var Layers = function Layers(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24.001",
    height: "23.996",
    viewBox: "0 0 24.001 23.996"
  }), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0.001 -0.001)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M12,13.5a2.3,2.3,0,0,1-1.052-.254L.738,7.96a1.365,1.365,0,0,1,0-2.426L10.948.256a2.3,2.3,0,0,1,2.1,0l10.21,5.279a.777.777,0,0,1,.107.068,1.366,1.366,0,0,1-.107,2.364L13.053,13.241A2.3,2.3,0,0,1,12,13.5ZM1.632,6.761l10.005,5.148a.8.8,0,0,0,.727,0L22.348,6.75,12.364,1.588a.8.8,0,0,0-.728,0Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0.001 -0.001)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M12,18.75a2.282,2.282,0,0,1-1.052-.256L.738,13.213a1.365,1.365,0,0,1,0-2.426L4.755,8.709a.75.75,0,0,1,.69,1.332L1.632,12.014l10.005,5.148a.8.8,0,0,0,.727,0l10-5.176-3.812-1.945a.75.75,0,1,1,.689-1.332l4.017,2.078a1.366,1.366,0,0,1,0,2.426L13.053,18.494A2.3,2.3,0,0,1,12,18.75Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0.001 -0.001)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M12,24a2.282,2.282,0,0,1-1.052-.256L.738,18.46a1.365,1.365,0,0,1,0-2.426l4.017-2.075a.75.75,0,1,1,.688,1.332l-3.812,1.97,10.005,5.148a.8.8,0,0,0,.727,0l10-5.17-3.811-1.948a.75.75,0,1,1,.689-1.332l4.017,2.081a1.366,1.366,0,0,1,0,2.426L13.053,23.741A2.289,2.289,0,0,1,12,24Z"
  })));
};

var LinkBroken = function LinkBroken(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "22.498",
    height: "24",
    viewBox: "0 0 22.498 24"
  }), /*#__PURE__*/React.createElement("g", {
    transform: "translate(-0.751 0.001)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M18.543,16.5c-.066,0-.133,0-.2-.005H16.5a.75.75,0,0,1,0-1.5h1.875a3.23,3.23,0,0,0,3.376-3.039A3.229,3.229,0,0,0,18.407,9H16.5a.75.75,0,0,1,0-1.5h1.875a4.72,4.72,0,0,1,4.874,4.46A4.744,4.744,0,0,1,18.543,16.5Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(-0.751 0.001)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M5.429,16.5A4.724,4.724,0,0,1,.751,12.04,4.765,4.765,0,0,1,5.657,7.5H7.5A.75.75,0,0,1,7.5,9H5.625a3.233,3.233,0,0,0-3.376,3.04A3.229,3.229,0,0,0,5.593,15H7.5a.75.75,0,0,1,0,1.5H5.625C5.56,16.5,5.494,16.5,5.429,16.5Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(-0.751 0.001)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M12,6a.75.75,0,0,1-.75-.75V.749a.75.75,0,1,1,1.5,0v4.5A.75.75,0,0,1,12,6Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(-0.751 0.001)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M9,6a.744.744,0,0,1-.53-.22l-1.5-1.5a.75.75,0,0,1,1.06-1.06l1.5,1.5A.75.75,0,0,1,9,6Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(-0.751 0.001)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M15,6a.75.75,0,0,1-.53-1.28l1.5-1.5a.75.75,0,1,1,1.06,1.06l-1.5,1.5A.744.744,0,0,1,15,6Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(-0.751 0.001)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M12,24a.75.75,0,0,1-.75-.75v-4.5a.75.75,0,0,1,1.5,0v4.5A.75.75,0,0,1,12,24Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(-0.751 0.001)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M7.5,21a.75.75,0,0,1-.53-1.28l1.5-1.5a.75.75,0,0,1,1.06,1.06l-1.5,1.5A.744.744,0,0,1,7.5,21Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(-0.751 0.001)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M16.5,21a.744.744,0,0,1-.53-.22l-1.5-1.5a.75.75,0,0,1,1.06-1.06l1.5,1.5A.75.75,0,0,1,16.5,21Z"
  })));
};

var Linked = function Linked(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24",
    height: "10.5",
    viewBox: "0 0 24 10.5"
  }), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0 -7.25)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M6.752,17.75H5.25a5.25,5.25,0,0,1,0-10.5h1.5A5.23,5.23,0,0,1,10.643,9a.751.751,0,1,1-1.122,1A3.72,3.72,0,0,0,6.749,8.75H5.25a3.75,3.75,0,0,0,0,7.5h1.5A3.729,3.729,0,0,0,9.522,15a.75.75,0,1,1,1.12,1A5.234,5.234,0,0,1,6.752,17.75Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0 -7.25)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M18.75,17.75h-1.5A5.24,5.24,0,0,1,13.357,16a.751.751,0,1,1,1.122-1,3.726,3.726,0,0,0,2.773,1.249h1.5a3.75,3.75,0,0,0,0-7.5h-1.5A3.718,3.718,0,0,0,14.479,10a.751.751,0,0,1-1.122-1A5.229,5.229,0,0,1,17.249,7.25h1.5a5.25,5.25,0,0,1,0,10.5Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0 -7.25)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M17.25,13.249H6.75a.75.75,0,0,1,0-1.5h10.5a.75.75,0,0,1,0,1.5Z"
  })));
};

var Linked$1 = function Linked(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "18",
    height: "24",
    viewBox: "0 0 18 24"
  }), /*#__PURE__*/React.createElement("g", {
    transform: "translate(-3)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M18.75,24H5.25A2.253,2.253,0,0,1,3,21.75V11.25A2.252,2.252,0,0,1,5.25,9h13.5A2.253,2.253,0,0,1,21,11.25v10.5A2.253,2.253,0,0,1,18.75,24ZM5.25,10.5a.75.75,0,0,0-.75.75v10.5a.751.751,0,0,0,.75.75h13.5a.752.752,0,0,0,.75-.75V11.25a.751.751,0,0,0-.75-.75Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(-3)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M17.25,10.5a.75.75,0,0,1-.75-.75V6a4.5,4.5,0,0,0-9,0V9.75a.75.75,0,0,1-1.5,0V6A6,6,0,0,1,18,6V9.75A.75.75,0,0,1,17.25,10.5Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(-3)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M12,18.75a.75.75,0,0,1-.75-.75V15a.75.75,0,0,1,1.5,0v3A.75.75,0,0,1,12,18.75Z"
  })));
};

var Minus = function Minus(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24",
    height: "1.5",
    viewBox: "0 0 24 1.5"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M23.25,12.788H.75a.75.75,0,0,1,0-1.5h22.5a.75.75,0,0,1,0,1.5Z",
    transform: "translate(0 -11.288)"
  }));
};

var MoreHorizontal = function MoreHorizontal(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "21",
    height: "5",
    viewBox: "0 0 21 5"
  }), /*#__PURE__*/React.createElement("circle", {
    fill: "currentColor",
    cx: "2.5",
    cy: "2.5",
    r: "2.5"
  }), /*#__PURE__*/React.createElement("circle", {
    fill: "currentColor",
    cx: "2.5",
    cy: "2.5",
    r: "2.5",
    transform: "translate(8)"
  }), /*#__PURE__*/React.createElement("circle", {
    fill: "currentColor",
    cx: "2.5",
    cy: "2.5",
    r: "2.5",
    transform: "translate(16)"
  }));
};

var MoreVertical = function MoreVertical(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "5",
    height: "21",
    viewBox: "0 0 5 21"
  }), /*#__PURE__*/React.createElement("g", {
    transform: "translate(10583 -362) rotate(90)"
  }, /*#__PURE__*/React.createElement("circle", {
    fill: "currentColor",
    cx: "2.5",
    cy: "2.5",
    r: "2.5",
    transform: "translate(362 10578)"
  }), /*#__PURE__*/React.createElement("circle", {
    fill: "currentColor",
    cx: "2.5",
    cy: "2.5",
    r: "2.5",
    transform: "translate(370 10578)"
  }), /*#__PURE__*/React.createElement("circle", {
    fill: "currentColor",
    cx: "2.5",
    cy: "2.5",
    r: "2.5",
    transform: "translate(378 10578)"
  })));
};

var Multiselect = function Multiselect(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24",
    height: "23.999",
    viewBox: "0 0 24 23.999"
  }), /*#__PURE__*/React.createElement("g", {
    transform: "translate(-13.334 -13.036)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M27.978,24.2a1.747,1.747,0,0,1-.278-.023,1.665,1.665,0,0,1-1.088-.687L24.187,20.06a.563.563,0,0,1,.128-.779.548.548,0,0,1,.77.13l2.425,3.433a.57.57,0,0,0,.9.033l7.944-9.641a.547.547,0,0,1,.777-.071.562.562,0,0,1,.071.786l-7.944,9.641a1.735,1.735,0,0,1-.312.293A1.653,1.653,0,0,1,27.978,24.2Z"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M33.469,30.9H22.428a2.779,2.779,0,0,1-2.76-2.792V16.944a2.779,2.779,0,0,1,2.76-2.791h9.385a.558.558,0,0,1,0,1.116H22.428a1.668,1.668,0,0,0-1.656,1.675V28.109a1.667,1.667,0,0,0,1.656,1.675H33.469a1.667,1.667,0,0,0,1.656-1.675V20.852a.552.552,0,1,1,1.1,0v7.257A2.779,2.779,0,0,1,33.469,30.9Z"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M30.257,33.9H19.216a2.78,2.78,0,0,1-2.761-2.792V19.941a2.779,2.779,0,0,1,2.761-2.79h.5a.558.558,0,0,1,0,1.116h-.5a1.667,1.667,0,0,0-1.657,1.674V31.106a1.668,1.668,0,0,0,1.657,1.676H30.257a1.668,1.668,0,0,0,1.656-1.676v-.579a.552.552,0,1,1,1.1,0v.579A2.779,2.779,0,0,1,30.257,33.9Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(-13.334 -13.036)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M27.136,37.035H16.094a2.779,2.779,0,0,1-2.76-2.791V23.079a2.779,2.779,0,0,1,2.76-2.792h.5a.559.559,0,0,1,0,1.117h-.5a1.667,1.667,0,0,0-1.656,1.675V34.244a1.668,1.668,0,0,0,1.656,1.675H27.136a1.668,1.668,0,0,0,1.656-1.675v-.58a.552.552,0,1,1,1.1,0v.58A2.779,2.779,0,0,1,27.136,37.035Z"
  })));
};

var NounPlatform = function NounPlatform(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "19.301",
    height: "19.301",
    viewBox: "0 0 19.301 19.301"
  }), /*#__PURE__*/React.createElement("g", {
    transform: "translate(-4.85 -13.025)"
  }, /*#__PURE__*/React.createElement("g", {
    transform: "translate(4.85 13.025)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M21.8,20.328a.811.811,0,0,0-.427-.761l-3.312-1.547,3.312-1.547a.892.892,0,0,0,0-1.522l-8.98-4.222a.566.566,0,0,0-.488,0l-8.98,4.222a.892.892,0,0,0,0,1.522l3.291,1.547L2.927,19.567a.892.892,0,0,0,0,1.522l3.291,1.547L2.927,24.182a.892.892,0,0,0,0,1.522l8.98,4.222a.437.437,0,0,0,.244.049.861.861,0,0,0,.244-.049l8.98-4.222a.892.892,0,0,0,0-1.522l-3.312-1.547,3.312-1.547A.811.811,0,0,0,21.8,20.328ZM12.15,12.35l7.131,3.339L12.15,19.052,5.019,15.713Zm7.131,12.593L12.15,28.307,5.019,24.943,8.087,23.52l3.82,1.792a.437.437,0,0,0,.244.049.861.861,0,0,0,.244-.049l3.82-1.792ZM12.15,23.667,5.019,20.328,8.067,18.88l3.82,1.792a.437.437,0,0,0,.244.049.861.861,0,0,0,.244-.049l3.84-1.792,3.047,1.448Z",
    transform: "translate(-2.5 -10.675)"
  }))));
};

var Pencil = function Pencil(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "23.996",
    height: "24",
    viewBox: "0 0 23.996 24"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M.75,24.005a.752.752,0,0,1-.727-.937l1.77-6.907a.758.758,0,0,1,.2-.344l14.5-14.5a4.4,4.4,0,0,1,6.231-.037l.015.017a4.4,4.4,0,0,1-.055,6.222L8.185,22.014a.747.747,0,0,1-.344.2l-6.9,1.772A.8.8,0,0,1,.75,24.005Zm2.445-7.273-1.4,5.481L7.27,20.808,21.625,6.45a2.9,2.9,0,0,0,.055-4.09l.51-.55-.529.532a2.893,2.893,0,0,0-4.086.008Z",
    transform: "translate(0 -0.005)"
  }), /*#__PURE__*/React.createElement("g", {
    transform: "translate(15.856 1.504)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M21.74,8.145a.744.744,0,0,1-.53-.22L16.076,2.79a.75.75,0,0,1,1.061-1.061l5.134,5.136a.75.75,0,0,1-.531,1.28Z",
    transform: "translate(-15.856 -1.51)"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(1.771 15.592)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M7.66,22.225a.753.753,0,0,1-.53-.219L1.991,16.875a.75.75,0,1,1,1.06-1.062l5.139,5.13a.751.751,0,0,1-.53,1.282Z",
    transform: "translate(-1.771 -15.594)"
  })));
};

var Plus = function Plus(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "23.996",
    height: "24",
    viewBox: "0 0 23.996 24"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M.75,24.005a.752.752,0,0,1-.727-.937l1.77-6.907a.758.758,0,0,1,.2-.344l14.5-14.5a4.4,4.4,0,0,1,6.231-.037l.015.017a4.4,4.4,0,0,1-.055,6.222L8.185,22.014a.747.747,0,0,1-.344.2l-6.9,1.772A.8.8,0,0,1,.75,24.005Zm2.445-7.273-1.4,5.481L7.27,20.808,21.625,6.45a2.9,2.9,0,0,0,.055-4.09l.51-.55-.529.532a2.893,2.893,0,0,0-4.086.008Z",
    transform: "translate(0 -0.005)"
  }), /*#__PURE__*/React.createElement("g", {
    transform: "translate(15.856 1.504)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M21.74,8.145a.744.744,0,0,1-.53-.22L16.076,2.79a.75.75,0,0,1,1.061-1.061l5.134,5.136a.75.75,0,0,1-.531,1.28Z",
    transform: "translate(-15.856 -1.51)"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(1.771 15.592)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M7.66,22.225a.753.753,0,0,1-.53-.219L1.991,16.875a.75.75,0,1,1,1.06-1.062l5.139,5.13a.751.751,0,0,1-.53,1.282Z",
    transform: "translate(-1.771 -15.594)"
  })));
};

var QuestionMark = function QuestionMark(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "12",
    height: "21.6",
    viewBox: "0 0 12 21.6"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M14.25,20.85a1.2,1.2,0,0,1-1.2-1.2V18.04a3.6,3.6,0,0,1,2.4-3.4,3.6,3.6,0,1,0-4.8-3.4,1.2,1.2,0,0,1-1.2,1.2h0a1.2,1.2,0,0,1-1.2-1.2,6,6,0,1,1,8,5.658,1.2,1.2,0,0,0-.8,1.131V19.65A1.2,1.2,0,0,1,14.25,20.85Z",
    transform: "translate(-8.25 -5.25)"
  }), /*#__PURE__*/React.createElement("g", {
    transform: "translate(4.2 18)"
  }, /*#__PURE__*/React.createElement("circle", {
    fill: "currentColor",
    cx: "1.8",
    cy: "1.8",
    r: "1.8"
  })));
};

var RemoveSubtract = function RemoveSubtract(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24",
    height: "24",
    viewBox: "0 0 24 24"
  }), /*#__PURE__*/React.createElement("g", {
    transform: "translate(4.125 10.875)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M21.375,13.5H7.875a1.125,1.125,0,0,1,0-2.25h13.5a1.125,1.125,0,0,1,0,2.25Z",
    transform: "translate(-6.75 -11.25)"
  })), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M12,24A12,12,0,1,1,24,12,12.013,12.013,0,0,1,12,24ZM12,1.5A10.5,10.5,0,1,0,22.5,12,10.512,10.512,0,0,0,12,1.5Z"
  }));
};

var School = function School(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24",
    height: "24",
    viewBox: "0 0 24 24"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M7.5,24H.75A.75.75,0,0,1,0,23.25V15a2.253,2.253,0,0,1,2.25-2.25H7.5a.75.75,0,0,1,0,1.5H2.25A.751.751,0,0,0,1.5,15v7.5h6a.75.75,0,0,1,0,1.5Z"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M23.25,24H16.5a.75.75,0,0,1,0-1.5h6V15a.752.752,0,0,0-.75-.75H16.5a.75.75,0,0,1,0-1.5h5.25A2.253,2.253,0,0,1,24,15v8.25A.75.75,0,0,1,23.25,24Z"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M13.5,24h-3a.75.75,0,0,1-.75-.75V19.313a2.2,2.2,0,0,1,2.3-2.062,2.162,2.162,0,0,1,2.2,2.01c0,.017,0,.035,0,.052V23.25A.75.75,0,0,1,13.5,24Zm-2.25-1.5h1.5V19.345a.673.673,0,0,0-.7-.6.688.688,0,0,0-.8.618Z"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M12,7.5a.75.75,0,0,1-.75-.75v-6a.75.75,0,0,1,1.5,0v6A.75.75,0,0,1,12,7.5Z"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M16,4.5H12a.75.75,0,0,1-.75-.75v-3A.75.75,0,0,1,12,0h4a1.252,1.252,0,0,1,1.25,1.25v2A1.252,1.252,0,0,1,16,4.5ZM12.75,3h3V1.5h-3Z"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M12,13.5a2.217,2.217,0,1,1,2.217-2.217A2.22,2.22,0,0,1,12,13.5Zm0-2.934a.717.717,0,1,0,.717.717A.718.718,0,0,0,12,10.566Z"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M16.5,24h-9a.75.75,0,0,1-.75-.75V11.118A5.174,5.174,0,0,1,12.011,6a5.168,5.168,0,0,1,5.239,5.107V23.25A.75.75,0,0,1,16.5,24ZM8.25,22.5h7.5V11.124A3.69,3.69,0,0,0,12.066,7.5h-.111a3.694,3.694,0,0,0-3.7,3.629Z"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M4.5,17.25H3.75a.75.75,0,0,1,0-1.5H4.5a.75.75,0,0,1,0,1.5Z"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M4.5,21H3.75a.75.75,0,0,1,0-1.5H4.5a.75.75,0,0,1,0,1.5Z"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M20.25,17.25H19.5a.75.75,0,0,1,0-1.5h.75a.75.75,0,0,1,0,1.5Z"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M20.25,21H19.5a.75.75,0,0,1,0-1.5h.75a.75.75,0,0,1,0,1.5Z"
  }));
};

var Search = function Search(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24.003",
    height: "24.003",
    viewBox: "0 0 24.003 24.003"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M8771.72,23798.781l-6.518-6.518a9.888,9.888,0,0,1-6.383,2.367,9.859,9.859,0,0,1-3.674-.717,9.815,9.815,0,1,1,11.119-2.715l6.513,6.52a.742.742,0,0,1,0,1.063.75.75,0,0,1-.525.221A.766.766,0,0,1,8771.72,23798.781Zm-16.15-21.621a8.313,8.313,0,1,0,9.069,13.586c.016-.02.033-.039.052-.059l.053-.047a8.311,8.311,0,0,0-9.174-13.48Zm-.286-.687h-.012Z",
    transform: "translate(-8748.999 -23775)"
  }));
};

var Share = function Share(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24",
    height: "22.4",
    viewBox: "0 0 24 22.4"
  }), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0 5.6)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M5.55,16.35a4.8,4.8,0,1,1,4.8-4.8A4.806,4.806,0,0,1,5.55,16.35Zm0-8a3.2,3.2,0,1,0,3.2,3.2A3.2,3.2,0,0,0,5.55,8.35Z",
    transform: "translate(-0.75 -6.75)"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(14.4)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M19.05,11.1a4.8,4.8,0,1,1,4.8-4.8A4.806,4.806,0,0,1,19.05,11.1Zm0-8a3.2,3.2,0,1,0,3.2,3.2A3.2,3.2,0,0,0,19.05,3.1Z",
    transform: "translate(-14.25 -1.5)"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(14.4 12.8)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M19.05,23.1a4.8,4.8,0,1,1,4.8-4.8A4.806,4.806,0,0,1,19.05,23.1Zm0-8a3.2,3.2,0,1,0,3.2,3.2A3.2,3.2,0,0,0,19.05,15.1Z",
    transform: "translate(-14.25 -13.5)"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(7.729 5.451)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M8.8,10.91a.8.8,0,0,1-.29-1.546l6.942-2.7a.8.8,0,1,1,.579,1.491l-6.941,2.7A.79.79,0,0,1,8.8,10.91Z",
    transform: "translate(-7.996 -6.61)"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(7.578 11.39)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M15.9,17.4a.787.787,0,0,1-.356-.084L8.3,13.694a.8.8,0,0,1,.715-1.431l7.242,3.621A.8.8,0,0,1,15.9,17.4Z",
    transform: "translate(-7.855 -12.178)"
  })));
};

var Social = function Social(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24",
    height: "18.75",
    viewBox: "0 0 24 18.75"
  }), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0 -2.625)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M18,9.375H15.033a.75.75,0,0,1,0-1.5H18a.75.75,0,0,1,0,1.5Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0 -2.625)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M20.25,12.375H15a.75.75,0,0,1,0-1.5h5.25a.75.75,0,0,1,0,1.5Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0 -2.625)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M8.854,11.625a3,3,0,1,1,3-3A3,3,0,0,1,8.854,11.625Zm0-4.5a1.5,1.5,0,1,0,1.5,1.5A1.5,1.5,0,0,0,8.854,7.125Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0 -2.625)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M12.75,14.625a.75.75,0,0,1-.649-.375,3.751,3.751,0,0,0-6.495,0,.75.75,0,1,1-1.3-.75,5.249,5.249,0,0,1,9.091,0,.75.75,0,0,1-.274,1.024A.739.739,0,0,1,12.75,14.625Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0 -2.625)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M.75,21.375a.75.75,0,0,1-.7-1.014L1.732,15.87A8.176,8.176,0,0,1,0,10.879,8.292,8.292,0,0,1,8.248,2.625h7.5a8.25,8.25,0,0,1,0,16.5H8.25l-.062,0L.971,21.342A.764.764,0,0,1,.75,21.375Zm7.5-17.25A6.788,6.788,0,0,0,1.5,10.877a6.669,6.669,0,0,0,1.651,4.356.751.751,0,0,1,.138.758l-1.3,3.468,5.9-1.814a.723.723,0,0,1,.221-.034c.037,0,.144.01.18.015H15.75a6.75,6.75,0,0,0,0-13.5Z"
  })));
};

var SortedAscending = function SortedAscending(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24",
    height: "15.389",
    viewBox: "0 0 24 15.389"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M278.334,200.917l-11.3,11.294a2.4,2.4,0,0,0,0,3.393,2.135,2.135,0,0,0,1.464.693c1.639,0,16.5.01,19.432.01a2.4,2.4,0,0,0,1.7-4.1Z",
    transform: "translate(-266.334 -200.917)"
  }));
};

var SortedDescending = function SortedDescending(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24",
    height: "15.393",
    viewBox: "0 0 24 15.393"
  }), /*#__PURE__*/React.createElement("g", {
    transform: "translate(-266.334 -200.917)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M278.334,216.31l-11.3-11.3a2.4,2.4,0,0,1,0-3.393,2.135,2.135,0,0,1,1.464-.694c1.639,0,16.5-.01,19.432-.01a2.4,2.4,0,0,1,1.7,4.1Z",
    transform: "translate(0 0)"
  })));
};

var Star = function Star(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24.001",
    height: "24.001",
    viewBox: "0 0 24.001 24.001"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M18.9,24a1.577,1.577,0,0,1-.7-.166L12,20.771,5.813,23.835a1.563,1.563,0,0,1-2.209-1.8l1.851-6.721L.462,10.37a1.556,1.556,0,0,1,.965-2.655l6.019-.6L10.6.862a1.568,1.568,0,0,1,2.8,0v0L16.56,7.124l6.029.6a1.555,1.555,0,0,1,.952,2.655l-4.986,4.942L20.4,22.033A1.565,1.565,0,0,1,18.9,24ZM12,19.184a.75.75,0,0,1,.333.078L18.862,22.5l.818-.263-.723.2-1.965-7.139a.746.746,0,0,1,.195-.731l5.3-5.254-.046-.095L16,8.576a.754.754,0,0,1-.6-.409L12.061,1.541a.059.059,0,0,0-.028-.028L8.6,8.163a.754.754,0,0,1-.6.409l-6.44.637L.99,9.837,1.518,9.3l5.3,5.253a.751.751,0,0,1,.2.732L5.052,22.427h0l.095.064h0l6.52-3.229A.75.75,0,0,1,12,19.184Z",
    transform: "translate(-0.002 -0.003)"
  }));
};

var StarSolid = function StarSolid(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24.001",
    height: "24.001",
    viewBox: "0 0 24.001 24.001"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M18.9,24a1.577,1.577,0,0,1-.7-.166L12,20.771,5.813,23.835a1.563,1.563,0,0,1-2.209-1.8l1.851-6.721L.462,10.37a1.556,1.556,0,0,1,.965-2.655l6.019-.6L10.6.862a1.568,1.568,0,0,1,2.8,0v0L16.56,7.124l6.029.6a1.555,1.555,0,0,1,.952,2.655l-4.986,4.942L20.4,22.033A1.565,1.565,0,0,1,18.9,24Z",
    transform: "translate(-0.002 -0.003)"
  }));
};

var Success = function Success(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24",
    height: "24",
    viewBox: "0 0 24 24"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M20.18,9.534a.857.857,0,0,1,0,1.217l-7.714,7.714a.857.857,0,0,1-.609.249h-.051a.857.857,0,0,1-.617-.326L7.76,14.1A.857.857,0,1,1,9.1,13.04l2.829,3.531,7.037-7.037a.857.857,0,0,1,1.217,0ZM26,14A12,12,0,1,1,14,2,12,12,0,0,1,26,14Zm-1.714,0A10.286,10.286,0,1,0,14,24.286,10.286,10.286,0,0,0,24.286,14Z",
    transform: "translate(-2 -2)"
  }));
};

var Trash = function Trash(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24",
    height: "23.999",
    viewBox: "0 0 24 23.999"
  }), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0 3.199)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M23.95,5.35H1.55a.8.8,0,0,1,0-1.6h22.4a.8.8,0,0,1,0,1.6Z",
    transform: "translate(-0.75 -3.75)"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(7.2)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M16.3,5.55h-8a.8.8,0,0,1-.8-.8V3.15A2.4,2.4,0,0,1,9.9.75h4.8a2.4,2.4,0,0,1,2.4,2.4v1.6A.8.8,0,0,1,16.3,5.55ZM9.1,3.95h6.4v-.8a.8.8,0,0,0-.8-.8H9.9a.8.8,0,0,0-.8.8Z",
    transform: "translate(-7.5 -0.75)"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(8.8 8.801)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M9.8,18.6a.8.8,0,0,1-.8-.8v-8a.8.8,0,0,1,1.6,0v8A.8.8,0,0,1,9.8,18.6Z",
    transform: "translate(-9 -9)"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(13.6 8.801)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M14.3,18.6a.8.8,0,0,1-.8-.8v-8a.8.8,0,1,1,1.6,0v8A.8.8,0,0,1,14.3,18.6Z",
    transform: "translate(-13.5 -9)"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(2.399 3.199)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M18.328,24.55H6.873a2.413,2.413,0,0,1-2.391-2.2L3,4.617a.8.8,0,0,1,.8-.867H21.4a.8.8,0,0,1,.8.867L20.719,22.349A2.413,2.413,0,0,1,18.328,24.55ZM4.669,5.35,6.076,22.216a.8.8,0,0,0,.8.734H18.328a.8.8,0,0,0,.8-.733h0l1.4-16.866Z",
    transform: "translate(-3 -3.75)"
  })));
};

var UserNeutral = function UserNeutral(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "21",
    height: "24",
    viewBox: "0 0 21 24"
  }), /*#__PURE__*/React.createElement("g", {
    transform: "translate(-1.5)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M12,12a6,6,0,1,1,6-6A6.006,6.006,0,0,1,12,12ZM12,1.5A4.5,4.5,0,1,0,16.5,6,4.505,4.505,0,0,0,12,1.5Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(-1.5)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M21.75,24a.75.75,0,0,1-.75-.75,9,9,0,0,0-18,0,.75.75,0,0,1-1.5,0,10.5,10.5,0,0,1,21,0A.75.75,0,0,1,21.75,24Z"
  })));
};

var VideoPlay = function VideoPlay(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "20.825",
    height: "21.989",
    viewBox: "0 0 20.825 21.989"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M3.838,23a2.253,2.253,0,0,1-2.25-2.25V3.255A2.249,2.249,0,0,1,4.9,1.272l16.324,8.745a2.251,2.251,0,0,1,0,3.966L4.9,22.729A2.27,2.27,0,0,1,3.838,23Zm0-20.49a.749.749,0,0,0-.662.395.76.76,0,0,0-.089.355v17.49a.772.772,0,0,0,1.106.66l16.322-8.744a.75.75,0,0,0,.306-1.015.742.742,0,0,0-.306-.307L4.193,2.594A.755.755,0,0,0,3.839,2.505Z",
    transform: "translate(-1.588 -1.006)"
  }));
};

var Warning = function Warning(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24",
    height: "24",
    viewBox: "0 0 24 24"
  }), /*#__PURE__*/React.createElement("g", {
    transform: "translate(11.249 4.5)"
  }, /*#__PURE__*/React.createElement("rect", {
    fill: "currentColor",
    width: "1.875",
    height: "10.805",
    transform: "translate(0 0)"
  }), /*#__PURE__*/React.createElement("rect", {
    fill: "currentColor",
    width: "1.875",
    height: "2.701",
    transform: "translate(0 12.137)"
  })), /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M12,24A12,12,0,0,1,3.513,3.514a12,12,0,1,1,16.97,16.97A11.923,11.923,0,0,1,12,24ZM12,1.5A10.5,10.5,0,0,0,4.575,19.423,10.5,10.5,0,0,0,19.424,4.575,10.438,10.438,0,0,0,12,1.5Z"
  }));
};

var WorldDisallow = function WorldDisallow(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24.002",
    height: "24.001",
    viewBox: "0 0 24.002 24.001"
  }), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0.002 0.001)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M17.25,24A6.75,6.75,0,1,1,24,17.25,6.758,6.758,0,0,1,17.25,24Zm0-12a5.25,5.25,0,1,0,5.25,5.25A5.256,5.256,0,0,0,17.25,12Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0.002 0.001)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M13.008,22.241a.75.75,0,0,1-.53-1.28l8.484-8.483a.75.75,0,0,1,1.06,1.06l-8.484,8.483A.744.744,0,0,1,13.008,22.241Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0.002 0.001)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M10.455,23.9a.737.737,0,0,1-.1-.007A12,12,0,1,1,23.9,10.4a.75.75,0,0,1-1.487.2A10.5,10.5,0,1,0,3.625,18.335,10.426,10.426,0,0,0,10.557,22.4a.751.751,0,0,1-.1,1.494Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0.002 0.001)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M9.29,23.671a.749.749,0,0,1-.621-.327C7,20.892,6,16.65,6,12S7,3.109,8.669.657a.75.75,0,0,1,1.24.844C8.423,3.682,7.5,7.7,7.5,12s.923,8.318,2.409,10.5a.75.75,0,0,1-.619,1.173Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0.002 0.001)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M10.5,12H.775a.75.75,0,0,1,0-1.5H10.5a.75.75,0,0,1,0,1.5Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0.002 0.001)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M21,6H3A.75.75,0,0,1,3,4.5H21A.75.75,0,0,1,21,6Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0.002 0.001)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M7.214,18H2.048a.75.75,0,1,1,0-1.5H7.214a.75.75,0,0,1,0,1.5Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0.002 0.001)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M17.017,8.992a.749.749,0,0,1-.746-.683,16.347,16.347,0,0,0-2.206-6.848A.75.75,0,0,1,15.356.7a17.878,17.878,0,0,1,2.409,7.479.751.751,0,0,1-.681.813C17.062,8.991,17.039,8.992,17.017,8.992Z"
  })));
};

var WorldNavigate = function WorldNavigate(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24.014",
    height: "24.014",
    viewBox: "0 0 24.014 24.014"
  }), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0.013 0.014)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M11.994,24A12.007,12.007,0,0,1,11.988-.014h.006A12.009,12.009,0,0,1,24,11.987a12.059,12.059,0,0,1-.122,1.711.75.75,0,1,1-1.484-.213,10.693,10.693,0,0,0,.106-1.5,10.507,10.507,0,0,0-10.507-10.5h-.005A10.507,10.507,0,0,0,11.995,22.5H12a10.612,10.612,0,0,0,1.484-.1.751.751,0,0,1,.207,1.487A12.36,12.36,0,0,1,12,24Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0.013 0.014)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M9.29,23.671a.749.749,0,0,1-.621-.327C7,20.892,6,16.651,6,12S7,3.11,8.669.657a.75.75,0,0,1,1.24.844C8.422,3.682,7.5,7.7,7.5,12s.923,8.319,2.41,10.5a.749.749,0,0,1-.2,1.042A.736.736,0,0,1,9.29,23.671Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0.013 0.014)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M11.249,12H.774a.75.75,0,0,1,0-1.5H11.249a.75.75,0,0,1,0,1.5Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0.013 0.014)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M23.249,12H16.5a.75.75,0,1,1,0-1.5h6.75a.75.75,0,0,1,0,1.5Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0.013 0.014)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M21,6H3A.75.75,0,0,1,3,4.5H21A.75.75,0,0,1,21,6Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0.013 0.014)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M10.5,18H2.047a.75.75,0,0,1,0-1.5H10.5a.75.75,0,0,1,0,1.5Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0.013 0.014)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M17.241,12a.75.75,0,0,1-.75-.731c-.007-.264-.016-.529-.03-.794C16.273,6.76,15.388,3.4,14.09,1.5A.75.75,0,1,1,15.33.657c1.467,2.154,2.426,5.7,2.629,9.743q.021.419.032.832A.751.751,0,0,1,17.26,12Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0.013 0.014)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M17.838,24A1.295,1.295,0,0,1,16.7,23.32l-4.129-9.015-.01-.023a1.313,1.313,0,0,1,1.718-1.719l9,4.119a1.315,1.315,0,0,1,.665.782,1.288,1.288,0,0,1-.862,1.6l-2.944,1.068L19.056,23.12a1.248,1.248,0,0,1-.609.723A1.282,1.282,0,0,1,17.838,24Zm-.2-1.37-.008.02Zm-3.487-8.475,3.66,7.993L18.846,19.3a.743.743,0,0,1,.45-.45l2.851-1.033Zm8.457,3.492h0Z"
  })));
};

var WorldTime = function WorldTime(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24.001",
    height: "24.002",
    viewBox: "0 0 24.001 24.002"
  }), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0.001 0.002)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M17.25,24A6.75,6.75,0,1,1,24,17.25,6.758,6.758,0,0,1,17.25,24Zm0-12a5.25,5.25,0,1,0,5.25,5.25A5.256,5.256,0,0,0,17.25,12Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0.001 0.002)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M19.9,18H17.25a.75.75,0,0,1-.75-.75V14.6a.75.75,0,0,1,1.5,0v1.9h1.9a.75.75,0,1,1,0,1.5Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0.001 0.002)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M10.495,23.9a.855.855,0,0,1-.1-.006A12,12,0,1,1,23.9,10.406a.75.75,0,0,1-.644.843.741.741,0,0,1-.843-.644A10.5,10.5,0,1,0,10.6,22.407a.75.75,0,0,1-.1,1.493Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0.001 0.002)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M9.29,23.671a.749.749,0,0,1-.621-.327C7,20.892,6,16.65,6,12S7,3.11,8.669.657a.75.75,0,0,1,1.24.844C8.423,3.683,7.5,7.706,7.5,12s.923,8.318,2.409,10.5a.75.75,0,0,1-.619,1.173Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0.001 0.002)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M10.3,12H.775a.75.75,0,0,1,0-1.5H10.3a.75.75,0,0,1,0,1.5Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0.001 0.002)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M21,6H3A.75.75,0,0,1,3,4.5H21A.75.75,0,0,1,21,6Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0.001 0.002)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M8.25,18h-6.2a.75.75,0,1,1,0-1.5h6.2a.75.75,0,0,1,0,1.5Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0.001 0.002)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M17.01,8.965a.751.751,0,0,1-.746-.683,16.321,16.321,0,0,0-2.2-6.821A.75.75,0,0,1,15.356.7a17.853,17.853,0,0,1,2.4,7.451.75.75,0,0,1-.681.814C17.055,8.964,17.032,8.965,17.01,8.965Z"
  })));
};

var WorldVisited = function WorldVisited(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  var clickProps;

  if (onClick) {
    clickProps = {
      onClick: onClick,
      tabindex: '0',
      role: 'button'
    };
  }

  return /*#__PURE__*/React.createElement("svg", Object.assign({
    className: classNames('icon', className)
  }, clickProps, {
    width: "24.014",
    height: "24.014",
    viewBox: "0 0 24.014 24.014"
  }), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0.013 0.014)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M11.994,24A12.007,12.007,0,0,1,11.988-.014h.006A12.009,12.009,0,0,1,24,11.987a12.059,12.059,0,0,1-.122,1.711.75.75,0,1,1-1.484-.213,10.693,10.693,0,0,0,.106-1.5,10.507,10.507,0,0,0-10.507-10.5h-.005A10.507,10.507,0,0,0,11.995,22.5H12a10.612,10.612,0,0,0,1.484-.1.751.751,0,0,1,.207,1.487A12.36,12.36,0,0,1,12,24Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0.013 0.014)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M9.29,23.671a.749.749,0,0,1-.621-.327C7,20.892,6,16.651,6,12S7,3.11,8.669.657a.75.75,0,0,1,1.24.844C8.422,3.682,7.5,7.7,7.5,12s.923,8.319,2.41,10.5a.749.749,0,0,1-.2,1.042A.736.736,0,0,1,9.29,23.671Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0.013 0.014)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M11.249,12H.774a.75.75,0,0,1,0-1.5H11.249a.75.75,0,0,1,0,1.5Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0.013 0.014)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M23.225,12H16.5a.75.75,0,1,1,0-1.5h6.726a.75.75,0,0,1,0,1.5Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0.013 0.014)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M21,6H3A.75.75,0,0,1,3,4.5H21A.75.75,0,0,1,21,6Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0.013 0.014)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M10.5,18H2.047a.75.75,0,0,1,0-1.5H10.5a.75.75,0,0,1,0,1.5Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0.013 0.014)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M17.241,12a.75.75,0,0,1-.75-.731c-.007-.264-.016-.529-.03-.794C16.273,6.76,15.388,3.4,14.09,1.5A.75.75,0,1,1,15.33.657c1.467,2.154,2.426,5.7,2.629,9.743q.021.419.032.832A.751.751,0,0,1,17.26,12Z"
  })), /*#__PURE__*/React.createElement("g", {
    transform: "translate(0.013 0.014)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M20.69,24a1.418,1.418,0,0,1-1-.413l-2.305-2.307-.57.571a1.423,1.423,0,0,1-1.678.245,1.412,1.412,0,0,1-.683-.844l-2.158-7.188a1.419,1.419,0,0,1,1.769-1.768l7.191,2.157a1.443,1.443,0,0,1,.6.356,1.423,1.423,0,0,1,0,2.006l-.571.569,2.306,2.306a1.416,1.416,0,0,1,0,2.006l-1.89,1.89A1.419,1.419,0,0,1,20.69,24Zm-3.307-4.53a.749.749,0,0,1,.53.22l2.777,2.778,1.776-1.776-2.778-2.778a.748.748,0,0,1,0-1.06L20.7,15.84l-6.926-2.067L15.854,20.7l1-1.014A.747.747,0,0,1,17.383,19.468ZM15.89,20.821Z"
  })));
};

export { Alert, Analytics as AnalyticsIcon, AppSwitch as AppSwitchIcon, ArrowDown as ArrowDownIcon, ArrowDownKPI as ArrowDownKPIIcon, ArrowLeft as ArrowLeftIcon, ArrowRight as ArrowRightIcon, ArrowUp as ArrowUpIcon, ArrowUpKPI as ArrowUpKPIIcon, Bell$1 as BellAlarmIcon, Bell as BellIcon, BookOpen as BookOpenIcon, Button, CalendarAlt as CalendarAltIcon, Calendar as CalendarIcon, Caution as CautionIcon, CeleratisConnector, Checkbox, CheckboxWithLabel, CheckmarkAlt as CheckmarkAltIcon, CheckmarkTall as CheckmarkTallIcon, ChevronDown as ChevronDownIcon, ChevronThickDown as ChevronThickDownIcon, ChevronThickLeft as ChevronThickLeftIcon, ChevronThickLeft$1 as ChevronThickRightIcon, ChevronThickUp as ChevronThickUpIcon, ChevronThickUp$1 as ChevronUpIcon, CircleLoader, Clock as ClockIcon, Close as CloseIcon, Close$1 as CloseThickIcon, Cog as CogIcon, Disallow as DisallowIcon, DownloadCloud as DownloadCloudIcon, DrawerEmpty as DrawerEmptyIcon, Drawer as DrawerIcon, Duplicate as DuplicateIcon, Edit as EditIcon, EmailSent as EmailSentIcon, ErrorMessage, ExpandDown as ExpandDownIcon, ExpandLeft as ExpandLeftIcon, ExpandRight as ExpandRightIcon, ExpandUp as ExpandUpIcon, Export as ExportIcon, ExternalLinkThick as ExternalLinkThickIcon, Favicon, Filter as FilterIcon, FilterPage as FilterPageIcon, FolderDirectory as FolderDirectoryIcon, Folder as FolderIcon, FolderOpen as FolderOpenIcon, GroupedTable, Hamburger as HamburgerIcon, Hamburger$1 as HandleHorizontalIcon, HandleVertical as HandleVerticalIcon, Header, Help as HelpIcon, Hierarchy as HierarchyIcon, Home as HomeIcon, I18n, Image as ImageIcon, Info as InfoIcon, Keys as KeysIcon, LargeTooltip, Layers as LayersIcon, LinkBroken as LinkBrokenIcon, LinkButton, Linked as LinkedIcon, Linked$1 as LockIcon, MediumTooltip, Minus as MinusIcon, MoreHorizontal as MoreHorizontalIcon, MoreVertical as MoreVerticalIcon, Multiselect as MultiselectIcon, NextButton, NounPlatform as NounPlatformIcon, PasswordInput, Pencil as PencilIcon, Plus as PlusIcon, PreviousButton, PrimaryButton, PrimaryStretchedButton, QuestionMark as QuestionMarkIcon, QuietSelect, Radio, RadioGroup, RemoveSubtract as RemoveSubtractIcon, School as SchoolIcon, Search as SearchIcon, Select, Share as ShareIcon, SmallButton, SmallTooltip, Social as SocialIcon, SortedAscending as SortedAscendingIcon, SortedDescending as SortedDescendingIcon, Star as StarIcon, StarSolid as StarSolidIcon, StretchedButton, Success as SuccessIcon, Tab, Tabs, TextInput, ToastNotification, Toggle, Tooltip, Trash as TrashIcon, UserMenu, UserNeutral as UserNeutralIcon, VideoPlay as VideoPlayIcon, View as ViewIcon, ViewNo as ViewNoIcon, Warning as WarningIcon, WorldDisallow as WorldDisallowIcon, World as WorldIcon, WorldNavigate as WorldNavigateIcon, WorldTime as WorldTimeIcon, WorldVisited as WorldVisitedIcon, classNames as cn, passwordErrorsList, passwordMeetsRequirements, useEventListener, useMediaQuery, useOnScreen, useUniqueId };
